/* eslint-disable react-hooks/exhaustive-deps, no-loop-func, no-mixed-operators, jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Button, Table, Accordion, Form, Card, Modal, Alert } from 'react-bootstrap';
import useAdvancedQuoteRules from './useAdvancedQuoteRules';
import { jockeyFirst } from './pumpRowOrder';
import { useNavigate, useParams } from 'react-router-dom';
import { usePortalApiRequest } from '../../../redux/api/portalApi';
import { selectApiUrls } from '../../../redux/slices/apiUrlSlice';
import { createSalesEndpoints } from '../api/salesApi';
import { isHyperlinkFieldType, normalizeHyperlinkHref } from './configurationAdminUtils';
const SALES_QUOTE_STATUS_LOOKUP_TYPE_ID = 17;
const toArray = response => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};
const toFiniteNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const firstItemOrObject = response => {
  const items = toArray(response);
  if (items.length > 0) {
    return items[0];
  }
  const payload = response?.data ?? response?.Data ?? response;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return payload;
  }
  return null;
};
const buildProjectStatusLookup = lookups => {
  const lookupMap = {};
  lookups.filter(lookup => lookup?.isActive !== false).forEach(lookup => {
    const directTypeId = Number(lookup?.lookupTypeId ?? lookup?.LookupTypeId ?? 0);
    const nestedTypeId = Number(lookup?.lookupType?.id ?? lookup?.lookupType?.Id ?? lookup?.LookupType?.id ?? lookup?.LookupType?.Id ?? 0);
    if (directTypeId !== SALES_QUOTE_STATUS_LOOKUP_TYPE_ID && nestedTypeId !== SALES_QUOTE_STATUS_LOOKUP_TYPE_ID) {
      return;
    }
    const label = String(lookup?.name ?? lookup?.Name ?? lookup?.description ?? lookup?.Description ?? lookup?.label ?? lookup?.Label ?? '').trim();
    if (!label) {
      return;
    }
    const valueKey = Number(lookup?.value ?? lookup?.Value);
    const idKey = Number(lookup?.id ?? lookup?.Id);
    const key = Number.isFinite(valueKey) ? valueKey : idKey;
    if (Number.isFinite(key)) {
      lookupMap[key] = label;
    }
  });
  return lookupMap;
};
const normalizeCostSummary = (source, fallback) => {
  if (!source || typeof source !== 'object') {
    return fallback;
  }
  return {
    ...fallback,
    listPrice: toFiniteNumber(source?.listPrice ?? source?.ListPrice, fallback.listPrice),
    stationPrice: toFiniteNumber(source?.stationPrice ?? source?.StationPrice, fallback.stationPrice),
    salesMultiplier: toFiniteNumber(source?.salesMultiplier ?? source?.SalesMultiplier, fallback.salesMultiplier),
    salesMultiplierAmount: toFiniteNumber(source?.salesMultiplierAmount ?? source?.SalesMultiplierAmount, fallback.salesMultiplierAmount),
    steelAdjPercent: toFiniteNumber(source?.steelAdjPercent ?? source?.SteelAdjPercent ?? source?.steelAjdPercent ?? source?.SteelAjdPercent, fallback.steelAdjPercent),
    steelAdjAmount: toFiniteNumber(source?.steelAdjAmount ?? source?.SteelAdjAmount ?? source?.steelAjd ?? source?.SteelAjd, fallback.steelAdjAmount),
    outsideCommissionPercent: toFiniteNumber(source?.outsideCommissionPercent ?? source?.OutsideCommissionPercent ?? source?.outSideCommissionPercent ?? source?.OutSideCommissionPercent, fallback.outsideCommissionPercent),
    outsideCommissionAmount: toFiniteNumber(source?.outsideCommissionAmount ?? source?.OutsideCommissionAmount ?? source?.outSideCommissionTotal ?? source?.OutSideCommissionTotal, fallback.outsideCommissionAmount),
    rebatesPercent: toFiniteNumber(source?.rebatesPercent ?? source?.RebatesPercent, fallback.rebatesPercent),
    rebatesAmount: toFiniteNumber(source?.rebatesAmount ?? source?.RebatesAmount ?? source?.rebates ?? source?.Rebates, fallback.rebatesAmount),
    freight: toFiniteNumber(source?.freight ?? source?.Freight, fallback.freight),
    overseasShipment: toFiniteNumber(source?.overseasShipment ?? source?.OverseasShipment, fallback.overseasShipment),
    setupAllowance: toFiniteNumber(source?.setupAllowance ?? source?.SetupAllowance, fallback.setupAllowance),
    startupAllowance: toFiniteNumber(source?.startupAllowance ?? source?.StartupAllowance, fallback.startupAllowance),
    craneRental: toFiniteNumber(source?.craneRental ?? source?.CraneRental, fallback.craneRental),
    inspectionFees: toFiniteNumber(source?.inspectionFees ?? source?.InspectionFees, fallback.inspectionFees),
    otherInstallationMisc: toFiniteNumber(source?.otherInstallationMisc ?? source?.OtherInstallationMisc ?? source?.otherMisc ?? source?.OtherMisc, fallback.otherInstallationMisc),
    setupMultiplierPercent: toFiniteNumber(source?.setupMultiplierPercent ?? source?.SetupMultiplierPercent ?? source?.setupMultiplier ?? source?.SetupMultiplier, fallback.setupMultiplierPercent),
    setupMultiplierAmount: toFiniteNumber(source?.setupMultiplierAmount ?? source?.SetupMultiplierAmount ?? source?.setupTotalValue ?? source?.SetupTotalValue, fallback.setupMultiplierAmount),
    taxes: toFiniteNumber(source?.taxes ?? source?.Taxes, fallback.taxes),
    warrantyType: (() => {
      const raw = source?.warrantyType ?? source?.WarrantyType;
      if (raw === 'Domestic' || raw === 'International') return raw;
      const typeId = Number(source?.warrantyTypeId ?? source?.WarrantyTypeId);
      if (typeId === 1) return 'Domestic';
      if (typeId === 2) return 'International';
      return fallback.warrantyType;
    })(),
    warrantyYears: toFiniteNumber(source?.warrantyYears ?? source?.WarrantyYears ?? source?.warrantyYearId ?? source?.WarrantyYearId, fallback.warrantyYears),
    warrantyAmount: toFiniteNumber(source?.warrantyAmount ?? source?.WarrantyAmount ?? source?.warrantyPrice ?? source?.WarrantyPrice, fallback.warrantyAmount),
    multiplierActive: toFiniteNumber(source?.multiplierActive ?? source?.MultiplierActive, fallback.multiplierActive),
    grossMarginPercent: toFiniteNumber(source?.grossMarginPercent ?? source?.GrossMarginPercent, fallback.grossMarginPercent),
    grossMarginAmount: toFiniteNumber(source?.grossMarginAmount ?? source?.GrossMarginAmount ?? source?.grossMargin ?? source?.GrossMargin, fallback.grossMarginAmount),
    netMarginPercent: toFiniteNumber(source?.netMarginPercent ?? source?.NetMarginPercent, fallback.netMarginPercent),
    netMarginAmount: toFiniteNumber(source?.netMarginAmount ?? source?.NetMarginAmount ?? source?.netMargin ?? source?.NetMargin, fallback.netMarginAmount),
    grossBaseAddItemMarginPercent: toFiniteNumber(source?.grossBaseAddItemMarginPercent ?? source?.GrossBaseAddItemMarginPercent, fallback.grossBaseAddItemMarginPercent),
    grossBaseAddItemMarginAmount: toFiniteNumber(source?.grossBaseAddItemMarginAmount ?? source?.GrossBaseAddItemMarginAmount ?? source?.grossBaseAddItemMargin ?? source?.GrossBaseAddItemMargin, fallback.grossBaseAddItemMarginAmount),
    netBaseAddItemMarginPercent: toFiniteNumber(source?.netBaseAddItemMarginPercent ?? source?.NetBaseAddItemMarginPercent, fallback.netBaseAddItemMarginPercent),
    netBaseAddItemMarginAmount: toFiniteNumber(source?.netBaseAddItemMarginAmount ?? source?.NetBaseAddItemMarginAmount ?? source?.netBaseAddItemMargin ?? source?.NetBaseAddItemMargin, fallback.netBaseAddItemMarginAmount),
    finalSalePrice: toFiniteNumber(source?.finalSalePrice ?? source?.FinalSalePrice ?? source?.totalCost ?? source?.TotalCost ?? source?.totalPrice ?? source?.TotalPrice, fallback.finalSalePrice),
    invoicePriceAsQuoted: toFiniteNumber(source?.invoicePriceAsQuoted ?? source?.InvoicePriceAsQuoted, fallback.invoicePriceAsQuoted)
  };
};
const normalizeYesNo = (value, defaultValue = 'No') => {
  if (typeof value === 'string') {
    const n = value.trim().toLowerCase();
    if (['yes', 'y', 'true', '1'].includes(n)) return 'Yes';
    if (['no', 'n', 'false', '0'].includes(n)) return 'No';
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value === 1 ? 'Yes' : 'No';
  return defaultValue;
};
const normalizeStationType = (raw, fallback) => {
  if (!raw || typeof raw !== 'object') return fallback;
  const dfp = raw?.dischargeFilterPresent ?? raw?.DischargeFilterPresent ?? raw?.dischargeFilterPercent ?? raw?.DischargeFilterPercent ?? fallback.dischargeFilterPresent;
  const dischargeFilterPresent = dfp === true || dfp === 1 || String(dfp).toUpperCase() === 'YES' ? 'YES' : 'NO';
  const uvRaw = raw?.uvFilter ?? raw?.UvFilter ?? raw?.uvfilter ?? fallback.uvFilter;
  const uvFilter = uvRaw === true || uvRaw === 1 || String(uvRaw).toUpperCase() === 'YES' ? 'YES' : 'NO';
  return {
    stationType: String(raw?.stationType ?? raw?.StationType ?? raw?.stationName ?? raw?.StationName ?? fallback.stationType),
    zones: toFiniteNumber(raw?.zones ?? raw?.Zones, fallback.zones),
    configuration: String(raw?.configuration ?? raw?.Configuration ?? fallback.configuration),
    primeSystem: String(raw?.primeSystem ?? raw?.PrimeSystem ?? fallback.primeSystem),
    panelOrKit: String(raw?.panelOrKit ?? raw?.PanelOrKit ?? fallback.panelOrKit),
    type: String(raw?.type ?? raw?.Type ?? fallback.type),
    vtTypeId: String(raw?.vtTypeId ?? raw?.VtTypeId ?? fallback.vtTypeId),
    dynamicInletPsi: toFiniteNumber(raw?.dynamicIntelPsi ?? raw?.DynamicIntelPsi ?? raw?.dynamicInletPsi ?? raw?.DynamicInletPsi, fallback.dynamicInletPsi),
    feetOfLift: toFiniteNumber(raw?.feetOfLift ?? raw?.FeetOfLift, fallback.feetOfLift),
    dischargePsi: toFiniteNumber(raw?.dischargePsi ?? raw?.DischargePsi ?? raw?.disChargePsi ?? raw?.DisChargePsi, fallback.dischargePsi),
    dischargeFilterPresent,
    totalGpm: toFiniteNumber(raw?.totalGpm ?? raw?.TotalGpm, fallback.totalGpm),
    uvFilter,
    siteVoltage: String(raw?.siteVoltage ?? raw?.SiteVoltage ?? fallback.siteVoltage),
    stationLocation: String(raw?.stationLocation ?? raw?.StationLocation ?? fallback.stationLocation),
    sccrRating: toFiniteNumber(raw?.sccrRating ?? raw?.SccrRating ?? raw?.sccrrating ?? raw?.Sccrrating, fallback.sccrRating),
    fccType: String(raw?.fccType ?? raw?.FccType ?? fallback.fccType),
    controlSystemPlatform: String(raw?.controlSystemPlatform ?? raw?.ControlSystemPlatform ?? fallback.controlSystemPlatform),
    mainPanelLocation: String(raw?.mainPanelLocation ?? raw?.MainPanelLocation ?? fallback.mainPanelLocation),
    flowMeterLocation: String(raw?.flowMeterLocation ?? raw?.FlowMeterLocation ?? fallback.flowMeterLocation),
    wetWellDepthFt: toFiniteNumber(raw?.wetWellDepthFt ?? raw?.WetWellDepthFt, fallback.wetWellDepthFt),
    wetWellDepthIn: toFiniteNumber(raw?.wetWellDepthIn ?? raw?.WetWellDepthIn ?? raw?.wetWellDepthInh ?? raw?.WetWellDepthInh, fallback.wetWellDepthIn),
    vtPumpBottomClearance: toFiniteNumber(raw?.vtPumpBottomClearance ?? raw?.VtPumpBottomClearance ?? raw?.vtpumpBottomClearance ?? raw?.VtpumpBottomClearance, fallback.vtPumpBottomClearance),
    pumpColumnType: String(raw?.pumpColumnType ?? raw?.PumpColumnType ?? fallback.pumpColumnType),
    baseMaterial: String(raw?.baseMaterial ?? raw?.BaseMaterial ?? fallback.baseMaterial),
    stationColor: String(raw?.stationColor ?? raw?.StationColor ?? fallback.stationColor),
    baseDimensionL: toFiniteNumber(raw?.baseDimensionLen ?? raw?.BaseDimensionLen ?? raw?.baseDimensionL ?? raw?.BaseDimensionL, fallback.baseDimensionL),
    baseDimensionW: toFiniteNumber(raw?.baseDimensionWht ?? raw?.BaseDimensionWht ?? raw?.baseDimensionW ?? raw?.BaseDimensionW, fallback.baseDimensionW)
  };
};
const normalizePumpPayload = (p, fallback, index) => ({
  id: toFiniteNumber(p?.id ?? p?.Id, index + 1),
  qty: toFiniteNumber(p?.quantity ?? p?.qty ?? p?.Quantity ?? p?.Qty, fallback.qty),
  pumpDescription: String(p?.pumpDescription ?? p?.pumpDescId ?? p?.pumpDesc ?? p?.PumpDescription ?? fallback.pumpDescription),
  motorHP: String(p?.motorHP ?? p?.motorHPId ?? p?.MotorHP ?? fallback.motorHP),
  type: String(p?.type ?? p?.pumpType ?? p?.pumpTypeId ?? p?.PumpType ?? fallback.type),
  gpm: toFiniteNumber(p?.gpm ?? p?.Gpm, fallback.gpm),
  vfd: normalizeYesNo(p?.vfd ?? p?.vfdId ?? p?.Vfd, fallback.vfd === 'Yes' ? 'Yes' : 'No'),
  xl: normalizeYesNo(p?.xl ?? p?.Xl, fallback.xl),
  ss: normalizeYesNo(p?.ss ?? p?.Ss, fallback.ss),
  ebv: normalizeYesNo(p?.ebv ?? p?.Ebv, fallback.ebv),
  zone: toFiniteNumber(p?.zone ?? p?.Zone, fallback.zone ?? 1)
});
const normalizeOptionPayload = (o, index) => ({
  id: toFiniteNumber(o?.id ?? o?.Id, index + 1),
  optionNumber: String(o?.optionNumber ?? o?.OptionNumber ?? o?.partNumber ?? o?.PartNumber ?? ''),
  category: String(o?.category ?? o?.Category ?? ''),
  partName: String(o?.partName ?? o?.PartName ?? o?.name ?? o?.Name ?? o?.description ?? o?.Description ?? ''),
  quantity: toFiniteNumber(o?.quantity ?? o?.qty ?? o?.Quantity, 1),
  listPrice: toFiniteNumber(o?.listPrice ?? o?.ListPrice ?? o?.price ?? o?.Price, 0),
  extendedPrice: toFiniteNumber(o?.extendedPrice ?? o?.ExtendedPrice, 0),
  base: Boolean(o?.base ?? o?.Base ?? o?.isBase ?? o?.IsBase ?? false),
  addl: Boolean(o?.addl ?? o?.Addl ?? o?.isAddl ?? o?.IsAddl ?? !Boolean(o?.base ?? o?.Base ?? false))
});
const normalizeSalesQuoteDetail = (payload, fallback, statusLookup = {}, costSummarySource) => {
  const project = payload?.project ?? payload?.Project ?? {};
  const contact = payload?.contact ?? payload?.Contact ?? project?.contact ?? project?.Contact ?? {};
  const company = payload?.company ?? payload?.Company ?? contact?.company ?? contact?.Company ?? project?.company ?? project?.Company ?? {};
  const saleRep = payload?.saleRep ?? payload?.SaleRep ?? project?.saleRep ?? project?.SaleRep ?? {};
  const createdBy = payload?.createdBy ?? payload?.CreatedBy;
  const contactFullName = String(payload?.contactName ?? payload?.ContactName ?? `${contact?.firstName ?? contact?.FirstName ?? ''} ${contact?.lastName ?? contact?.LastName ?? ''}`.trim() ?? fallback.generalInfo.contactName);
  const saleRepName = String(payload?.salesRep ?? payload?.SalesRep ?? saleRep?.fullName ?? saleRep?.FullName ?? `${saleRep?.firstName ?? saleRep?.FirstName ?? ''} ${saleRep?.lastName ?? saleRep?.LastName ?? ''}`.trim() ?? fallback.generalInfo.salesRep);
  const projectCity = String(project?.city ?? project?.City ?? '');
  const projectState = String(project?.state ?? project?.State ?? '');
  const projectLocationFallback = `${projectCity}${projectCity && projectState ? ', ' : ''}${projectState}`;
  const projectLocation = String(payload?.projectLocation ?? payload?.ProjectLocation ?? projectLocationFallback ?? fallback.generalInfo.projectLocation);
  const statusId = Number(payload?.statusId ?? payload?.StatusId ?? payload?.statusID ?? payload?.StatusID ?? NaN);
  const resolvedStatus = Number.isFinite(statusId) ? statusLookup[statusId] || payload?.status || payload?.Status || fallback.status : String(payload?.status ?? payload?.Status ?? fallback.status);
  const payloadCostSummary = costSummarySource ?? payload?.costSummary ?? payload?.CostSummary ?? firstItemOrObject(payload?.costSummaries) ?? firstItemOrObject(payload?.CostSummaries);
  const rawST = payload?.stationTypes ?? payload?.StationTypes ?? payload?.stationType ?? payload?.StationType;
  const rawPumps = toArray(payload?.pumpConfigurations ?? payload?.PumpConfigurations);
  const rawOptions = toArray(payload?.selectedOptions ?? payload?.SelectedOptions ?? payload?.selectedOptionList ?? payload?.SelectedOptionList);
  const emptyPump = {
    id: 1,
    qty: 1,
    pumpDescription: '',
    motorHP: '',
    type: '',
    gpm: 0,
    vfd: 'No',
    xl: 'No',
    ss: 'No',
    ebv: 'No',
    zone: 1
  };
  return {
    ...fallback,
    id: Number(payload?.id ?? payload?.Id ?? fallback.id),
    quotationNumber: String(payload?.quotationNumber ?? payload?.QuotationNumber ?? payload?.quoteNumber ?? payload?.QuoteNumber ?? fallback.quotationNumber),
    status: String(resolvedStatus),
    modelNumber: String(payload?.modelNumber ?? payload?.ModelNumber ?? fallback.modelNumber),
    isProposalGenerated: Boolean(payload?.isProposalGenerated ?? payload?.IsProposalGenerated ?? fallback.isProposalGenerated),
    notes: String(payload?.notes ?? payload?.Notes ?? fallback.notes),
    pumpSummary: {
      stationOnlyEstimatedFla: toFiniteNumber(payload?.fla ?? payload?.Fla ?? payload?.stationOnlyEstimatedFla, fallback.pumpSummary.stationOnlyEstimatedFla),
      modelNumber: String(payload?.modelNumber ?? payload?.ModelNumber ?? fallback.pumpSummary.modelNumber)
    },
    costSummary: normalizeCostSummary(payloadCostSummary, fallback.costSummary),
    stationType: normalizeStationType(rawST, fallback.stationType),
    pumpConfigurations: rawPumps.length > 0 ? rawPumps.map((p, i) => normalizePumpPayload(p, fallback.pumpConfigurations[i] ?? emptyPump, i)) : fallback.pumpConfigurations,
    selectedOptionList: rawOptions.length > 0 ? rawOptions.map((o, i) => normalizeOptionPayload(o, i)) : fallback.selectedOptionList,
    generalInfo: {
      ...fallback.generalInfo,
      date: String(payload?.createdDate ?? payload?.CreatedDate ?? payload?.date ?? payload?.Date ?? fallback.generalInfo.date),
      projectName: String(project?.name ?? project?.Name ?? payload?.projectName ?? payload?.ProjectName ?? fallback.generalInfo.projectName),
      projectLocation,
      contactName: contactFullName,
      contactCompany: String(company?.name ?? company?.Name ?? payload?.contactCompany ?? payload?.ContactCompany ?? fallback.generalInfo.contactCompany),
      contactNumber: String(contact?.phoneNumber ?? contact?.PhoneNumber ?? contact?.contactNumber ?? contact?.ContactNumber ?? project?.ContactPhoneNumber ?? project?.contactPhoneNumber ?? payload?.contactPhoneNumber ?? payload?.ContactPhoneNumber ?? fallback.generalInfo.contactNumber),
      contactEmail: String(contact?.email ?? contact?.Email ?? project?.ContactEmail ?? project?.contactEmail ?? payload?.contactEmail ?? payload?.ContactEmail ?? fallback.generalInfo.contactEmail),
      contactAddress: String(contact?.streetAddress ?? contact?.StreetAddress ?? project?.ContactAddress ?? project?.contactAddress ?? payload?.contactAddress ?? payload?.ContactAddress ?? fallback.generalInfo.contactAddress),
      contactCity: String(contact?.city ?? contact?.City ?? project?.ContactCity ?? project?.contactCity ?? payload?.contactCity ?? payload?.ContactCity ?? fallback.generalInfo.contactCity),
      contactState: String(contact?.state ?? contact?.State ?? project?.ContactState ?? project?.contactState ?? payload?.contactState ?? payload?.ContactState ?? fallback.generalInfo.contactState),
      salesRep: saleRepName,
      quotedBy: String(createdBy?.fullName ?? createdBy?.FullName ?? createdBy?.name ?? createdBy?.Name ?? payload?.quotedBy ?? payload?.QuotedBy ?? fallback.generalInfo.quotedBy)
    }
  };
};
const STATION_TYPE_FIELD_LABELS = {
  stationType: 'Station Type',
  zones: 'Zones',
  configuration: 'Configuration',
  primeSystem: 'Prime System',
  panelOrKit: 'Panel or Kit',
  type: 'Type',
  vtTypeId: 'VT Type',
  dynamicInletPsi: 'Dynamic Inlet PSI',
  feetOfLift: 'Feet of Lift',
  dischargePsi: 'Discharge PSI',
  dischargeFilterPresent: 'Discharge Filter Present',
  totalGpm: 'Total GPM',
  uvFilter: 'UV Filter',
  siteVoltage: 'Site Voltage',
  stationLocation: 'Station Location',
  sccrRating: 'SCCR Rating',
  fccType: 'FCC Type',
  controlSystemPlatform: 'Control System Platform',
  mainPanelLocation: 'Main Panel Location',
  flowMeterLocation: 'Flow Meter Location',
  wetWellDepthFt: 'Wet Well Depth (Ft)',
  wetWellDepthIn: 'Wet Well Depth (In)',
  vtPumpBottomClearance: 'VT Pump Bottom Clearance',
  pumpColumnType: 'Pump Column Type',
  baseMaterial: 'Base Material',
  stationColor: 'Station Color',
  baseDimensionL: 'Base Dimension L (in)',
  baseDimensionW: 'Base Dimension W (in)'
};
const STATION_TYPE_FIELD_SELF_QUALIFIERS = {
  stationType: 'stationType',
  configuration: 'configuration',
  panelOrKit: 'panelOrKit',
  type: 'type',
  vtTypeId: 'vtTypeId'
};
const STATION_TYPE_NUMERIC_FIELDS = new Set(['zones', 'dynamicInletPsi', 'feetOfLift', 'dischargePsi', 'totalGpm', 'sccrRating', 'wetWellDepthFt', 'wetWellDepthIn', 'vtPumpBottomClearance', 'baseDimensionL', 'baseDimensionW']);
const STATION_TYPE_YES_NO_FIELDS = new Set(['dischargeFilterPresent', 'uvFilter']);
const normalizeComparableValue = value => String(value ?? '').trim().replace(/\s+/g, '').toLowerCase();
const normalizeTextValue = value => String(value ?? '').trim();
const getStationTypeConfigContext = stationType => ({
  stationType: stationType.stationType,
  configuration: stationType.configuration,
  panelOrKit: stationType.panelOrKit,
  type: stationType.type,
  vtTypeId: stationType.vtTypeId
});
const isStationTypeFieldEmpty = (fieldKey, value) => {
  if (STATION_TYPE_NUMERIC_FIELDS.has(fieldKey)) {
    return Number(value) === 0 || Number.isNaN(Number(value));
  }
  return String(value ?? '').trim().length === 0;
};
const matchesStationConfigValue = (configValue, selectedValue, ignoreQualifier = false) => {
  if (ignoreQualifier) {
    return true;
  }
  const normalizedConfigValue = normalizeComparableValue(configValue);
  if (!normalizedConfigValue || normalizedConfigValue === 'all') {
    return true;
  }
  const normalizedSelectedValue = normalizeComparableValue(selectedValue);
  return normalizedSelectedValue.length > 0 && normalizedConfigValue === normalizedSelectedValue;
};
const dedupeSelectOptions = options => {
  const optionMap = new Map();
  options.forEach(option => {
    const normalizedValue = normalizeComparableValue(option.value);
    if (!normalizedValue) {
      return;
    }
    const existing = optionMap.get(normalizedValue);
    if (!existing) {
      optionMap.set(normalizedValue, {
        value: option.value,
        label: option.label ?? option.value,
        disabled: option.disabled ?? false
      });
      return;
    }
    if (!(option.disabled ?? false)) {
      existing.disabled = false;
    }
  });
  return Array.from(optionMap.values());
};
const resolveStationTypeFieldConfig = (configs, context, fieldKey) => {
  const fieldLabel = normalizeComparableValue(STATION_TYPE_FIELD_LABELS[fieldKey]);
  const selfQualifier = STATION_TYPE_FIELD_SELF_QUALIFIERS[fieldKey];
  const fieldConfigs = configs.filter(config => {
    if (normalizeComparableValue(config.page) !== 'quote') {
      return false;
    }
    if (normalizeComparableValue(config.section) !== 'stationtype') {
      return false;
    }
    return normalizeComparableValue(config.field) === fieldLabel;
  });
  if (fieldConfigs.length === 0) {
    return null;
  }
  const stationScopedConfigs = fieldConfigs.filter(config => matchesStationConfigValue(config.stationType, context.stationType, selfQualifier === 'stationType'));
  if (stationScopedConfigs.length === 0) {
    return null;
  }
  const matchingConfigs = stationScopedConfigs.filter(config => {
    if (!matchesStationConfigValue(config.hcvmsQualifier, context.configuration, selfQualifier === 'configuration')) {
      return false;
    }
    if (!matchesStationConfigValue(config.cpPanelKit, context.panelOrKit, selfQualifier === 'panelOrKit')) {
      return false;
    }
    if (!matchesStationConfigValue(config.cpKitType, context.type, selfQualifier === 'type')) {
      return false;
    }
    if (!matchesStationConfigValue(config.vtvqType, context.vtTypeId, selfQualifier === 'vtTypeId')) {
      return false;
    }
    return true;
  });
  if (matchingConfigs.length === 0) {
    const hasQualifierDependencies = stationScopedConfigs.some(config => {
      const qualifiers = [['configuration', config.hcvmsQualifier], ['panelOrKit', config.cpPanelKit], ['type', config.cpKitType], ['vtTypeId', config.vtvqType]];
      return qualifiers.some(([qualifierKey, qualifierValue]) => qualifierKey !== selfQualifier && Boolean(normalizeComparableValue(qualifierValue)) && normalizeComparableValue(qualifierValue) !== 'all');
    });
    return {
      isConfigured: true,
      isHidden: hasQualifierDependencies,
      isDisabled: true,
      defaultValue: null,
      options: []
    };
  }
  const optionRows = matchingConfigs.filter(config => normalizeComparableValue(config.fieldType) === 'dropdown').map(config => normalizeTextValue(config.value)).filter(Boolean).map(value => {
    const matchingRow = matchingConfigs.find(config => normalizeComparableValue(config.value) === normalizeComparableValue(value));
    return {
      value,
      label: value,
      disabled: matchingRow?.disabled ?? false
    };
  });
  return {
    isConfigured: true,
    isHidden: matchingConfigs.every(config => config.isHidden),
    isDisabled: matchingConfigs.every(config => config.disabled),
    defaultValue: matchingConfigs.find(config => config.isDefaultValue && !config.isHidden)?.value ?? null,
    options: dedupeSelectOptions(optionRows)
  };
};
const matchesStationTypeConfigContext = (config, context) => (
  normalizeComparableValue(config.page) === 'quote' &&
  normalizeComparableValue(config.section) === 'stationtype' &&
  matchesStationConfigValue(config.stationType, context.stationType) &&
  matchesStationConfigValue(config.hcvmsQualifier, context.configuration) &&
  matchesStationConfigValue(config.cpPanelKit, context.panelOrKit) &&
  matchesStationConfigValue(config.cpKitType, context.type) &&
  matchesStationConfigValue(config.vtvqType, context.vtTypeId)
);
const resolveStationTypeHyperlinks = (configs, context) => {
  const links = new Map();
  configs
    .filter(config => isHyperlinkFieldType(config.fieldType))
    .filter(config => !config.disabled && !config.isHidden)
    .filter(config => matchesStationTypeConfigContext(config, context))
    .forEach(config => {
      const label = normalizeTextValue(config.field);
      const href = normalizeHyperlinkHref(config.value);
      if (!label || !href) return;
      const key = `${normalizeComparableValue(label)}::${href}`;
      if (!links.has(key)) links.set(key, { label, href });
    });
  return Array.from(links.values()).sort((left, right) => left.label.localeCompare(right.label));
};
const getEmptyStationTypeFieldValue = fieldKey => {
  if (STATION_TYPE_NUMERIC_FIELDS.has(fieldKey)) {
    return 0;
  }
  if (STATION_TYPE_YES_NO_FIELDS.has(fieldKey)) {
    return 'NO';
  }
  return '';
};
const coerceStationTypeFieldValue = (fieldKey, value) => {
  if (STATION_TYPE_NUMERIC_FIELDS.has(fieldKey)) {
    return Number(value);
  }
  if (STATION_TYPE_YES_NO_FIELDS.has(fieldKey)) {
    return normalizeComparableValue(value) === 'yes' ? 'YES' : 'NO';
  }
  return value;
};
const defaultQuoteData = {
  id: 0,
  quotationNumber: '',
  status: '',
  modelNumber: '',
  isProposalGenerated: false,
  isSaveEnabled: false,
  warningEnable: false,
  hasSalesRepError: false,
  generalInfo: {
    date: '',
    projectName: '',
    projectLocation: '',
    contactName: '',
    contactCompany: '',
    contactNumber: '',
    contactEmail: '',
    contactAddress: '',
    contactCity: '',
    contactState: '',
    salesRep: '',
    quotedBy: ''
  },
  stationType: {
    stationType: '',
    zones: 1,
    configuration: '',
    primeSystem: '',
    panelOrKit: '',
    type: '',
    vtTypeId: '',
    dynamicInletPsi: 0,
    feetOfLift: 0,
    dischargePsi: 0,
    dischargeFilterPresent: 'NO',
    totalGpm: 0,
    uvFilter: 'NO',
    siteVoltage: '',
    stationLocation: '',
    sccrRating: 0,
    fccType: '',
    controlSystemPlatform: '',
    mainPanelLocation: '',
    flowMeterLocation: '',
    wetWellDepthFt: 0,
    wetWellDepthIn: 0,
    vtPumpBottomClearance: 0,
    pumpColumnType: '',
    baseMaterial: '',
    stationColor: '',
    baseDimensionL: 0,
    baseDimensionW: 0
  },
  pumpSummary: {
    stationOnlyEstimatedFla: 0,
    modelNumber: ''
  },
  pumpConfigurations: [],
  optionsSection: {
    categoryType: 'HIDE AUTO PICKS',
    category: '',
    description: ''
  },
  optionPickList: [],
  selectedOptionList: [],
  costSummary: {
    listPrice: 0,
    stationPrice: 0,
    salesMultiplier: 1,
    salesMultiplierAmount: 0,
    steelAdjPercent: 0,
    steelAdjAmount: 0,
    outsideCommissionPercent: 0,
    outsideCommissionAmount: 0,
    rebatesPercent: 0,
    rebatesAmount: 0,
    freight: 0,
    overseasShipment: 0,
    setupAllowance: 0,
    startupAllowance: 0,
    craneRental: 0,
    inspectionFees: 0,
    otherInstallationMisc: 0,
    setupMultiplierPercent: 1,
    setupMultiplierAmount: 0,
    taxes: 0,
    warrantyType: 'Domestic',
    warrantyYears: 1,
    warrantyAmount: 0,
    multiplierActive: 1,
    grossMarginPercent: 0,
    grossMarginAmount: 0,
    netMarginPercent: 0,
    netMarginAmount: 0,
    grossBaseAddItemMarginPercent: 0,
    grossBaseAddItemMarginAmount: 0,
    netBaseAddItemMarginPercent: 0,
    netBaseAddItemMarginAmount: 0,
    finalSalePrice: 0,
    invoicePriceAsQuoted: 0
  },
  notes: '',
  warningSorted: []
};
const SalesQuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const apiUrls = useSelector(selectApiUrls);
  const ENDPOINTS = useMemo(() => createSalesEndpoints(apiUrls), [apiUrls]);
  const portalApiRequest = usePortalApiRequest();
  const apiRequest = useMemo(() => ({ method = 'GET', url, data }) => portalApiRequest({ method: String(method).toLowerCase(), url, data }), [portalApiRequest]);
  const dispatch = useCallback(operation => operation, []);
  const endpointOperation = useMemo(() => (requestFactory) => ({ unwrap: () => requestFactory() }), []);
  const { lookupApi, userApi, salesQuoteApi, costSummaryApi, categoryApi, projectApi } = useMemo(() => ({
    lookupApi: {
      endpoints: {
        getLookups: { initiate: () => endpointOperation(() => apiRequest({ method: 'GET', url: ENDPOINTS.lookups.base })) },
        getSaleQuoteLookupData: { initiate: () => endpointOperation(async () => {
          const [stationModel, configurationModel, primeSystemModel] = await Promise.all([
            apiRequest({ method: 'GET', url: ENDPOINTS.stations.base }).then(response => toArray(response)).catch(() => []),
            apiRequest({ method: 'GET', url: ENDPOINTS.configurations.base }).then(response => toArray(response)).catch(() => []),
            apiRequest({ method: 'GET', url: ENDPOINTS.primeSystems.base }).then(response => toArray(response)).catch(() => []),
          ]);
          return { stationModel, configurationModel, primeSystemModel };
        }) },
        getStationTypeConfigs: { initiate: () => endpointOperation(() => apiRequest({ method: 'GET', url: ENDPOINTS.stationTypeConfigs.base })) },
      },
    },
    userApi: {
      endpoints: {
        getUsers: { initiate: () => endpointOperation(() => apiRequest({ method: 'GET', url: ENDPOINTS.users.base })) },
      },
    },
    salesQuoteApi: {
      endpoints: {
        getQuoteById: { initiate: (quoteId) => endpointOperation(async () => {
          const quotes = await apiRequest({ method: 'GET', url: ENDPOINTS.salesQuotes.base }).then(response => toArray(response));
          return quotes.find(item => Number(item?.id ?? item?.Id ?? item?.saleQuoteId ?? item?.SaleQuoteId ?? 0) === Number(quoteId)) ?? {};
        }) },
        createQuote: { initiate: (payload) => endpointOperation(() => apiRequest({ method: 'POST', url: ENDPOINTS.salesQuotes.base, data: payload })) },
        updateQuote: { initiate: ({ id: quoteId, data }) => endpointOperation(() => apiRequest({ method: 'PUT', url: ENDPOINTS.salesQuotes.byId(quoteId), data })) },
        getQuoteWarnings: { initiate: (payload) => endpointOperation(() => apiRequest({ method: 'POST', url: ENDPOINTS.salesQuotes.warnings, data: payload })) },
        generateProposal: { initiate: (payload) => endpointOperation(() => apiRequest({ method: 'POST', url: ENDPOINTS.salesQuotes.generateProposal, data: payload })) },
        updateCostSummarySalesQuote: { initiate: (payload) => endpointOperation(() => apiRequest({ method: 'POST', url: ENDPOINTS.salesQuotes.updateCostSummary, data: payload })) },
        generateModelNumber: { initiate: (payload) => endpointOperation(() => apiRequest({ method: 'POST', url: ENDPOINTS.salesQuotes.generateModelNumber, data: payload })) },
        getQuoteVersions: { initiate: (quoteId) => endpointOperation(async () => {
          const quotes = await apiRequest({ method: 'GET', url: ENDPOINTS.salesQuotes.base }).then(response => toArray(response));
          return quotes.filter(item => Number(item?.parentId ?? item?.ParentId ?? 0) === Number(quoteId));
        }) },
        createQuoteVersion: { initiate: (quoteId) => endpointOperation(() => apiRequest({ method: 'POST', url: ENDPOINTS.salesQuotes.createVersion(quoteId) })) },
        autopickQuote: { initiate: (payload) => endpointOperation(() => apiRequest({ method: 'POST', url: ENDPOINTS.salesQuotes.autopick, data: payload })) },
      },
    },
    costSummaryApi: {
      endpoints: {
        getCostSummariesBySaleQuote: { initiate: (quoteId) => endpointOperation(async () => {
          const summaries = await apiRequest({ method: 'GET', url: ENDPOINTS.costSummaries.base }).then(response => toArray(response));
          return summaries.filter(item => Number(item?.saleQuoteId ?? item?.SaleQuoteId ?? item?.sale_quote_id ?? 0) === Number(quoteId));
        }) },
      },
    },
    categoryApi: {
      endpoints: {
        getCategoriesByFilter: { initiate: ({ type, stationId, configId, primeSystemId }) => endpointOperation(async () => {
          const categories = await apiRequest({ method: 'GET', url: ENDPOINTS.categories.base }).then(response => toArray(response));
          return categories.filter(item => {
            const itemType = String(item?.categoryType ?? item?.CategoryType ?? item?.type ?? item?.Type ?? '');
            if (itemType && itemType !== String(type)) return false;
            const itemStationId = Number(item?.stationId ?? item?.StationId ?? 0);
            if (itemStationId > 0 && Number(stationId) > 0 && itemStationId !== Number(stationId)) return false;
            const itemConfigId = Number(item?.configurationId ?? item?.ConfigurationId ?? item?.configId ?? item?.ConfigId ?? 0);
            if (itemConfigId > 0 && Number(configId) > 0 && itemConfigId !== Number(configId)) return false;
            const itemPrimeId = Number(item?.primeSystemId ?? item?.PrimeSystemId ?? 0);
            if (itemPrimeId > 0 && Number(primeSystemId) > 0 && itemPrimeId !== Number(primeSystemId)) return false;
            return true;
          });
        }) },
        getPartsByCategory: { initiate: ({ categoryId, projectId }) => endpointOperation(async () => {
          const parts = await apiRequest({ method: 'GET', url: ENDPOINTS.parts.base }).then(response => toArray(response));
          return parts.filter(item => {
            const itemCategoryId = Number(item?.categoryId ?? item?.CategoryId ?? 0);
            if (itemCategoryId > 0 && Number(categoryId) > 0 && itemCategoryId !== Number(categoryId)) return false;
            const itemProjectId = Number(item?.projectId ?? item?.ProjectId ?? 0);
            if (Number(projectId) > 0 && itemProjectId > 0 && itemProjectId !== Number(projectId)) return false;
            return true;
          });
        }) },
        getAllEnclosurePricings: { initiate: () => endpointOperation(() => apiRequest({ method: 'GET', url: ENDPOINTS.enclosurePricings.base })) },
      },
    },
    projectApi: {
      endpoints: {
        getProjectById: { initiate: (projectIdValue) => endpointOperation(() => apiRequest({ method: 'GET', url: ENDPOINTS.projects.byId(projectIdValue) })) },
      },
    },
  }), [ENDPOINTS, apiRequest, endpointOperation]);
  const [quote, setQuote] = useState(defaultQuoteData);
  const advancedRules = useAdvancedQuoteRules();
  const advancedResult = useMemo(() => advancedRules.evaluate(quote), [advancedRules.evaluate, quote]);
  useEffect(() => {
    if (!quote.isProposalGenerated && advancedResult.updates.length) {
      setQuote(advancedResult.quote);
      setIsSaveEnabled(true);
    }
  }, [advancedResult, quote.isProposalGenerated]);
  const [activeKey, setActiveKey] = useState(['0', '1', '2', '3', '4']);
  const [isWarningExecuted, setIsWarningExecuted] = useState(false);
  const [lookupBuckets, setLookupBuckets] = useState({});
  const [userNames, setUserNames] = useState([]);
  const [stationModels, setStationModels] = useState([]);
  const [configurationModels, setConfigurationModels] = useState([]);
  const [primeSystemModels, setPrimeSystemModels] = useState([]);
  const [stationTypeConfigs, setStationTypeConfigs] = useState([]);
  const [projectId, setProjectId] = useState(0);
  const [isAutoPicking, setIsAutoPicking] = useState(false);
  const [autoPickErrors, setAutoPickErrors] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingWarnings, setIsLoadingWarnings] = useState(false);
  const [isLoadingCostSummary, setIsLoadingCostSummary] = useState(false);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [selectedOptionError, setSelectedOptionError] = useState(null);
  const [showPriceMatchModal, setShowPriceMatchModal] = useState(false);
  const [priceMatchInput, setPriceMatchInput] = useState('');
  const [showStationChangeWarning, setShowStationChangeWarning] = useState(false);
  const [showVtChangeWarning, setShowVtChangeWarning] = useState(false);
  const [pendingStationValue, setPendingStationValue] = useState('');
  const [pendingVtValue, setPendingVtValue] = useState('');
  const [previousVtValue, setPreviousVtValue] = useState('');
  const [autoPickFlag, setAutoPickFlag] = useState(false);
  const [autoPickCheckForWarning, setAutoPickCheckForWarning] = useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [versions, setVersions] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [enclosurePricingList, setEnclosurePricingList] = useState([]);
  const [showEditOptionModal, setShowEditOptionModal] = useState(false);
  const [editOptionIndex, setEditOptionIndex] = useState(-1);
  const [editOptionData, setEditOptionData] = useState({
    partName: '',
    description: ''
  });
  const [showCustomOptionModal, setShowCustomOptionModal] = useState(false);
  const [customOptionData, setCustomOptionData] = useState({
    name: '',
    listPrice: '',
    description: '',
    partNumber: ''
  });
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [prevStationValues, setPrevStationValues] = useState({});
  const onFormChange = updater => {
    setIsSaveEnabled(true);
    setQuote(previous => advancedRules.evaluate(typeof updater === 'function' ? updater(previous) : updater).quote);
  };
  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  const buildSalesQuotePayload = extra => {
    const stationId = getNamedIdByValue(stationModels, quote.stationType.stationType);
    const configurationId = getNamedIdByValue(configurationModels, quote.stationType.configuration);
    const primeSystemId = getNamedIdByValue(primeSystemModels, quote.stationType.primeSystem);
    const controlPanelorKitId = getLookupIdByLabel(['controlpanelorkit'], quote.stationType.panelOrKit);
    const controlPanelorKitTypeId = getLookupIdByLabel(['controlpanelorkittype'], quote.stationType.type);
    const eleSiteVoltageId = getLookupIdByLabel(['sitevoltage'], quote.stationType.siteVoltage);
    const siteLocationId = getLookupIdByLabel(['stationlocation'], quote.stationType.stationLocation);
    const fccTypeId = getLookupIdByLabel(['fcctype'], quote.stationType.fccType);
    const controlSystemPlatFormId = getLookupIdByLabel(['controlsystem'], quote.stationType.controlSystemPlatform);
    const mainPanelLocationId = getLookupIdByLabel(['mainpanel'], quote.stationType.mainPanelLocation);
    const flowMeterLocationId = getLookupIdByLabel(['flowmeter'], quote.stationType.flowMeterLocation);
    const pumpColumnTypeId = getLookupIdByLabel(['pumpcloumntype', 'pumpcolumntype'], quote.stationType.pumpColumnType);
    const baseMaterialId = getLookupIdByLabel(['basemeterial', 'basematerial'], quote.stationType.baseMaterial);
    const stationColorId = getLookupIdByLabel(['stationcolor'], quote.stationType.stationColor);
    const dischargeFilterPercent = quote.stationType.dischargeFilterPresent === 'YES' ? 1 : 0;
    return {
      id: quote.id || 0,
      notes: quote.notes,
      modelNumber: quote.modelNumber,
      fla: quote.pumpSummary.stationOnlyEstimatedFla,
      autoPickFlag,
      autopickHasError: autoPickErrors.length > 0,
      quoteType: quote.costSummary.warrantyType === 'Domestic' ? 1 : 2,
      stationTypes: {
        id: 0,
        stationId,
        configurationId,
        primeSystemId,
        controlPanelorKitId,
        controlPanelorKitTypeId,
        stationType: quote.stationType.stationType,
        zones: quote.stationType.zones,
        configuration: quote.stationType.configuration,
        primeSystem: quote.stationType.primeSystem,
        panelOrKit: quote.stationType.panelOrKit,
        type: quote.stationType.type,
        vtTypeId: quote.stationType.vtTypeId,
        dynamicInletPsi: quote.stationType.dynamicInletPsi,
        dynamicIntelPsi: quote.stationType.dynamicInletPsi,
        feetOfLift: quote.stationType.feetOfLift,
        dischargePsi: quote.stationType.dischargePsi,
        disChargePsi: quote.stationType.dischargePsi,
        dischargeFilterPresent: quote.stationType.dischargeFilterPresent,
        dischargeFilterPercent,
        totalGpm: quote.stationType.totalGpm,
        siteVoltage: quote.stationType.siteVoltage,
        eleSiteVoltageId,
        siteLocationId,
        sccrRating: quote.stationType.sccrRating,
        sccrrating: quote.stationType.sccrRating,
        fccTypeId,
        controlSystemPlatFormId,
        mainPanelLocationId,
        flowMeterLocationId,
        wetWellDepthFt: quote.stationType.wetWellDepthFt,
        wetWellDepthIn: quote.stationType.wetWellDepthIn,
        wetWellDepthInh: quote.stationType.wetWellDepthIn,
        vtPumpBottomClearance: quote.stationType.vtPumpBottomClearance,
        vtpumpBottomClearance: quote.stationType.vtPumpBottomClearance,
        pumpColumnTypeId,
        baseMaterialId,
        stationColorId,
        baseDimensionL: quote.stationType.baseDimensionL,
        baseDimensionLen: quote.stationType.baseDimensionL,
        baseDimensionW: quote.stationType.baseDimensionW,
        baseDimensionWht: quote.stationType.baseDimensionW,
        uvFilter: quote.stationType.uvFilter === 'YES' ? 1 : 0
      },
      pumpConfigurations: jockeyFirst(quote.pumpConfigurations).map(pump => ({
        quantity: pump.qty,
        pumpDescId: pump.pumpDescription,
        pumpDescription: pump.pumpDescription,
        motorHPId: pump.motorHP,
        motorHP: pump.motorHP,
        pumpTypeId: pump.type,
        pumpType: pump.type,
        gpm: pump.gpm,
        vfdId: pump.vfd,
        vfd: pump.vfd,
        xl: pump.xl,
        ss: pump.ss,
        ebv: pump.ebv,
        zone: pump.zone ?? 1
      })),
      selectedOptions: quote.selectedOptionList.map(option => ({
        optionNumber: option.optionNumber,
        category: option.category,
        partName: option.partName,
        quantity: option.quantity,
        listPrice: option.listPrice,
        extendedPrice: option.extendedPrice,
        base: option.base,
        addl: option.addl
      })),
      costSummaries: {
        id: 0,
        listPrice: quote.costSummary.listPrice,
        stationPrice: quote.costSummary.stationPrice,
        salesMultiplier: quote.costSummary.salesMultiplier,
        salesMultiplierAmount: quote.costSummary.salesMultiplierAmount,
        steelAjdPercent: quote.costSummary.steelAdjPercent,
        steelAjd: quote.costSummary.steelAdjAmount,
        outSideCommissionPercent: quote.costSummary.outsideCommissionPercent,
        outSideCommissionTotal: quote.costSummary.outsideCommissionAmount,
        rebatesPercent: quote.costSummary.rebatesPercent,
        rebates: quote.costSummary.rebatesAmount,
        freight: quote.costSummary.freight,
        overseasShipment: quote.costSummary.overseasShipment,
        setupAllowance: quote.costSummary.setupAllowance,
        startupAllowance: quote.costSummary.startupAllowance,
        craneRental: quote.costSummary.craneRental,
        inspectionFees: quote.costSummary.inspectionFees,
        otherMisc: quote.costSummary.otherInstallationMisc,
        setupMultiplier: quote.costSummary.setupMultiplierPercent,
        setupTotalValue: quote.costSummary.setupMultiplierAmount,
        taxes: quote.costSummary.taxes,
        warrantyTypeId: quote.costSummary.warrantyType === 'Domestic' ? 1 : 2,
        warrantyYearId: quote.costSummary.warrantyYears,
        warrantyPrice: quote.costSummary.warrantyAmount,
        multiplierActive: quote.costSummary.multiplierActive,
        finalSalePrice: quote.costSummary.finalSalePrice,
        invoicePriceAsQuoted: quote.costSummary.invoicePriceAsQuoted
      },
      ...extra
    };
  };
  const checkSelectedOptionsError = (selectedList, stationId, controlPanelorKitId, configurationId, controlSystemPlatFormId, _siteLocationId, hasEbv, _totalQuantity) => {
    const selectedNumbers = selectedList.map(o => o.optionNumber);
    const has = optNum => selectedNumbers.includes(optNum);
    const prefixBlocked = prefix => selectedNumbers.some(n => n.startsWith(prefix));
    if (controlSystemPlatFormId === 152) {
      const cat290qty = selectedList.filter(o => o.optionNumber.startsWith('290')).reduce((sum, o) => sum + o.quantity, 0);
      if (cat290qty > 1) return 'Only 1 option from category 290 is allowed for this Control System Platform.';
    }
    if ([1, 3].includes(configurationId)) {
      if (prefixBlocked('330') || prefixBlocked('370') || prefixBlocked('372')) {
        return 'Options 330, 370, 372 are not allowed for this configuration.';
      }
    }
    if (stationId === 5 && controlSystemPlatFormId === 143) {
      if (prefixBlocked('230') || prefixBlocked('300') || prefixBlocked('610') || prefixBlocked('620')) {
        return 'Options 230, 300, 610, 620 are not allowed for Station 5 with this control platform.';
      }
    }
    if (stationId === 10) {
      if (controlPanelorKitId === 272) {
        const blocked272_601 = ['601-0000003', '601-0000004', '601-0000005', '601-0000034', '601-0000037', '601-0000038', '601-0000039', '601-0000041', '601-0000045'];
        for (const opt of blocked272_601) {
          if (has(opt)) return `Option ${opt} is not allowed with this Panel/Kit selection.`;
        }
        if (has('650-0000002') || has('650-0000003')) return 'Options 650-0000002 and 650-0000003 are not allowed with this Panel/Kit.';
        const opt650_001 = selectedList.find(o => o.optionNumber === '650-0000001');
        if (opt650_001 && opt650_001.quantity > 1) return 'Option 650-0000001 quantity cannot exceed 1 with this Panel/Kit.';
        if (has('640-0000002')) return 'Option 640-0000002 is not allowed with this Panel/Kit.';
        const opt640_001 = selectedList.find(o => o.optionNumber === '640-0000001');
        if (opt640_001 && opt640_001.quantity > 1) return 'Option 640-0000001 quantity cannot exceed 1 with this Panel/Kit.';
      } else {
        if (has('601-0000040') || has('601-0000042')) {
          return 'Options 601-0000040 and 601-0000042 are not allowed with this Panel/Kit.';
        }
      }
      if (controlPanelorKitId === 271) {
        if (!hasEbv) {
          const blocked271_601 = ['601-0000001', '601-0000003', '601-0000009', '601-0000010', '601-0000011', '601-0000012', '601-0000013', '601-0000014', '601-0000015', '601-0000016', '601-0000017', '601-0000018', '601-0000019', '601-0000020', '601-0000021', '601-0000022', '601-0000023', '601-0000026', '601-0000027', '601-0000030', '601-0000031', '601-0000034', '601-0000035', '601-0000037', '601-0000038', '601-0000039', '601-0000040', '601-0000041', '601-0000042', '601-0000044'];
          const blocked271_998 = ['998-0000007', '998-0000008', '998-0000011', '998-0000012', '998-0000016', '998-0000017', '998-0000018', '998-0000019', '998-0000020', '998-0000021', '998-0000022', '998-0000023', '998-0000024', '998-0000025', '998-0000026', '998-0000027', '998-0000028', '998-0000029', '998-0000030', '998-0000041'];
          const blocked271_650 = ['650-0000002', '650-0000003'];
          const blocked271_640 = ['640-0000002'];
          for (const opt of [...blocked271_601, ...blocked271_998, ...blocked271_650, ...blocked271_640]) {
            if (has(opt)) return `Option ${opt} is not allowed for this Panel/Kit without EBV.`;
          }
        }
      } else if (controlPanelorKitId !== 272) {
        if (!hasEbv) {
          const blockedNoKit = ['601-0000001', '601-0000040', '601-0000042', '998-0000041'];
          for (const opt of blockedNoKit) {
            if (has(opt)) return `Option ${opt} is not allowed without EBV for this Panel/Kit.`;
          }
        }
      }
      if (has('720-0000001')) {
        const qty720 = selectedList.find(o => o.optionNumber === '720-0000001')?.quantity ?? 0;
        let maxQty = Infinity;
        if (controlPanelorKitId === 270) maxQty = hasEbv ? 3 : 4;else if (controlPanelorKitId === 269) maxQty = hasEbv ? 5 : 6;else if ([268, 281].includes(controlPanelorKitId)) maxQty = hasEbv ? 5 : 6;
        if (qty720 > maxQty) {
          return `Option 720-0000001 quantity exceeds the maximum allowed (${maxQty}) for this Panel/Kit and EBV configuration.`;
        }
      }
    }
    return null;
  };
  const loadWarnings = async () => {
    setIsLoadingWarnings(true);
    try {
      const payload = buildSalesQuotePayload({
        autoPickFlag: autoPickCheckForWarning || autoPickFlag
      });
      const result = await dispatch(salesQuoteApi.endpoints.getQuoteWarnings.initiate(payload)).unwrap();
      const rawList = Array.isArray(result) ? result : Array.isArray(result?.data) ? result.data : [];
      const sorted = rawList.map((w, index) => {
        const text = String(w?.warningText ?? w?.text ?? w?.message ?? w ?? '');
        const match = text.match(/\[(\d+)\]|^(\d+)/);
        const parsedId = match ? parseInt(match[1] ?? match[2] ?? '0', 10) : index + 1;
        const rawType = w?.type ?? w?.warningType ?? 3;
        const type = rawType === 1 ? 1 : rawType === 2 ? 2 : 3;
        return {
          id: parsedId,
          text,
          type
        };
      }).sort((a, b) => a.id - b.id);
      setQuote(previous => ({
        ...previous,
        warningSorted: sorted
      }));
      setIsWarningExecuted(true);
    } catch {
      setIsWarningExecuted(true);
    } finally {
      setIsLoadingWarnings(false);
    }
  };
  const salesQuoteSubmit = async (isProposal = false) => {
    if (quote.hasSalesRepError) return;
    const globalCheck = advancedRules.evaluate(quote);
    if (!globalCheck.isValid || globalCheck.updates.length) {
      setValidationMessage(globalCheck.errors.map(error => error.message).join('\n') || 'Automatic rules updated the configuration. Review it before saving.');
      setShowValidationModal(true);
      if (globalCheck.updates.length) setQuote(globalCheck.quote);
      return;
    }
    setIsSaving(true);
    try {
      const payload = buildSalesQuotePayload();
      let savedId = quote.id;
      if (quote.id && quote.id > 0) {
        await dispatch(salesQuoteApi.endpoints.updateQuote.initiate({
          id: quote.id,
          data: payload
        })).unwrap();
      } else {
        const created = await dispatch(salesQuoteApi.endpoints.createQuote.initiate(payload)).unwrap();
        savedId = created?.id ?? savedId;
        setQuote(previous => ({
          ...previous,
          id: savedId
        }));
      }
      if (isProposal && savedId) {
        await dispatch(salesQuoteApi.endpoints.generateProposal.initiate({
          ...payload,
          id: savedId
        })).unwrap();
        navigate(`/sales/quotes/${savedId}`);
        return;
      }
      setIsSaveEnabled(false);
      setQuote(previous => ({
        ...previous,
        isSaveEnabled: false
      }));
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  };
  const invokeCostSummaryApi = async (type, userPrice) => {
    if (quote.selectedOptionList.length === 0) {
      alert('Please select at least one option before recalculating the cost summary.');
      return;
    }
    setIsLoadingCostSummary(true);
    try {
      const typeFlag = {};
      if (type === 'up') typeFlag.up = true;else if (type === 'down') typeFlag.down = true;else if (type === '5%') typeFlag.fivePercent = true;else if (type === '10%') typeFlag.tenPercent = true;else if (type === 'isUserPrice') {
        typeFlag.isUserPrice = true;
        if (userPrice !== undefined) typeFlag.userPrice = userPrice;
      } else if (type === 'refresh') typeFlag.refresh = true;
      const payload = buildSalesQuotePayload({
        ...typeFlag
      });
      const result = await dispatch(salesQuoteApi.endpoints.updateCostSummarySalesQuote.initiate(payload)).unwrap();
      if (result && typeof result === 'object') {
        setQuote(previous => ({
          ...previous,
          costSummary: normalizeCostSummary(result, previous.costSummary)
        }));
      }
    } catch {} finally {
      setIsLoadingCostSummary(false);
    }
  };
  const updatedModelNumber = async () => {
    try {
      const payload = buildSalesQuotePayload();
      const result = await dispatch(salesQuoteApi.endpoints.generateModelNumber.initiate(payload)).unwrap();
      const newModel = String(result?.modelNumber ?? result?.data?.modelNumber ?? result?.ModelNumber ?? '');
      if (newModel) {
        setQuote(previous => ({
          ...previous,
          modelNumber: newModel,
          pumpSummary: {
            ...previous.pumpSummary,
            modelNumber: newModel
          }
        }));
      }
    } catch {}
  };
  const removePumpConfigurationItem = pumpId => {
    setIsSaveEnabled(true);
    setQuote(previous => ({
      ...previous,
      pumpConfigurations: previous.pumpConfigurations.filter(p => p.id !== pumpId)
    }));
  };
  const removeSelectedOption = optionIndex => {
    setIsSaveEnabled(true);
    setQuote(previous => ({
      ...previous,
      selectedOptionList: previous.selectedOptionList.filter((_, i) => i !== optionIndex)
    }));
  };
  const handleSelectOptions = async () => {
    const stationId = getNamedIdByValue(stationModels, quote.stationType.stationType);
    const controlPanelorKitId = getLookupIdByLabel(['controlpanelorkit'], quote.stationType.panelOrKit);
    const configurationId = getNamedIdByValue(configurationModels, quote.stationType.configuration);
    const controlSystemPlatFormId = getLookupIdByLabel(['controlsystem'], quote.stationType.controlSystemPlatform);
    const siteLocationId = getLookupIdByLabel(['stationlocation'], quote.stationType.stationLocation);
    const hasEbv = quote.pumpConfigurations.some(p => p.ebv === 'Yes');
    const totalQuantity = quote.pumpConfigurations.reduce((sum, p) => sum + (p.qty || 0), 0);
    const error = checkSelectedOptionsError(quote.selectedOptionList, stationId, controlPanelorKitId, configurationId, controlSystemPlatFormId, siteLocationId, hasEbv, totalQuantity);
    setSelectedOptionError(error);
    if (error) {
      setQuote(previous => ({
        ...previous,
        warningSorted: [{
          id: 0,
          text: error,
          type: 1
        }]
      }));
      return;
    }
    const has900 = quote.selectedOptionList.some(o => o.optionNumber.startsWith('900'));
    if (has900 && enclosurePricingList.length > 0) {
      const L = quote.stationType.baseDimensionL;
      const W = quote.stationType.baseDimensionW;
      const sizeMatch = enclosurePricingList.some(p => p.length === L && p.width === W || p.length === W && p.width === L);
      if (!sizeMatch) {
        const enclosureError = `No enclosure pricing found for base dimensions ${L} × ${W}. Please select valid base dimensions before adding option 900.`;
        setSelectedOptionError(enclosureError);
        setQuote(previous => ({
          ...previous,
          warningSorted: [{
            id: 0,
            text: enclosureError,
            type: 1
          }]
        }));
        return;
      }
    }
    setIsSaveEnabled(true);
    if (has900) {
      await updatedModelNumber();
    }
    await salesQuoteSubmit(false);
    await invokeCostSummaryApi('refresh');
  };
  const handleStationTypeChange = newValue => {
    if (quote.selectedOptionList.length > 0) {
      setPendingStationValue(newValue);
      setShowStationChangeWarning(true);
    } else {
      setIsSaveEnabled(true);
      setQuote(previous => ({
        ...previous,
        stationType: {
          ...previous.stationType,
          stationType: newValue
        }
      }));
    }
  };
  const confirmStationChange = () => {
    setShowStationChangeWarning(false);
    setIsSaveEnabled(true);
    setIsWarningExecuted(false);
    const newStation = pendingStationValue;
    setQuote(previous => ({
      ...previous,
      stationType: {
        ...previous.stationType,
        stationType: newStation
      },
      selectedOptionList: []
    }));
    setPendingStationValue('');
    const newStId = getNamedIdByValue(stationModels, newStation);
    const configId = getNamedIdByValue(configurationModels, quote.stationType.configuration);
    const primeId = getNamedIdByValue(primeSystemModels, quote.stationType.primeSystem);
    const apiType = quote.optionsSection.categoryType === 'HIDE AUTO PICKS' ? '1' : '2';
    loadCategoriesByType(apiType, newStId, configId, primeId);
  };
  const cancelStationChange = () => {
    setShowStationChangeWarning(false);
    setPendingStationValue('');
  };
  const handleVtTypeChange = newValue => {
    if (quote.selectedOptionList.length > 0) {
      setPreviousVtValue(quote.stationType.vtTypeId);
      setPendingVtValue(newValue);
      setShowVtChangeWarning(true);
    } else {
      setIsSaveEnabled(true);
      setQuote(previous => ({
        ...previous,
        stationType: {
          ...previous.stationType,
          vtTypeId: newValue
        }
      }));
    }
  };
  const confirmVtChange = () => {
    setShowVtChangeWarning(false);
    setIsSaveEnabled(true);
    setIsWarningExecuted(false);
    setQuote(previous => ({
      ...previous,
      stationType: {
        ...previous.stationType,
        vtTypeId: pendingVtValue
      },
      selectedOptionList: []
    }));
    setPendingVtValue('');
  };
  const cancelVtChange = () => {
    setShowVtChangeWarning(false);
    setQuote(previous => ({
      ...previous,
      stationType: {
        ...previous.stationType,
        vtTypeId: previousVtValue
      }
    }));
    setPendingVtValue('');
  };
  const getSalesQuoteVersions = async () => {
    if (!quote.id || quote.id <= 0) return;
    try {
      const result = await dispatch(salesQuoteApi.endpoints.getQuoteVersions.initiate(quote.id, {
        subscribe: false
      })).unwrap();
      setVersions(Array.isArray(result) ? result : []);
      setShowVersionsModal(true);
    } catch {
      setVersions([]);
      setShowVersionsModal(true);
    }
  };
  const createNewVersion = async () => {
    if (!quote.id || quote.id <= 0) return;
    try {
      const newQuote = await dispatch(salesQuoteApi.endpoints.createQuoteVersion.initiate(quote.id)).unwrap();
      const newId = newQuote?.id;
      if (newId) navigate(`/sales/quotes/${newId}`);
    } catch {}
  };
  const warningTextColor = type => {
    if (type === 1) return 'red';
    if (type === 2) return 'blue';
    return 'black';
  };
  const yesNo = value => value ? 'Yes' : 'No';
  const normalizeLookupKey = value => value.replace(/\s+/g, '').toLowerCase();
  const loadCategoriesByType = async (categoryType, overrideStationId, overrideConfigId, overridePrimeId) => {
    const stId = overrideStationId ?? getNamedIdByValue(stationModels, quote.stationType.stationType);
    const configId = overrideConfigId ?? getNamedIdByValue(configurationModels, quote.stationType.configuration);
    const primeId = overridePrimeId ?? getNamedIdByValue(primeSystemModels, quote.stationType.primeSystem);
    const typeToUse = categoryType || '1';
    try {
      const result = await dispatch(categoryApi.endpoints.getCategoriesByFilter.initiate({
        type: typeToUse,
        stationId: stId,
        configId,
        primeSystemId: primeId
      }, {
        subscribe: false
      })).unwrap();
      const cats = (Array.isArray(result) ? result : []).map(c => ({
        id: Number(c?.id ?? c?.Id ?? 0),
        name: String(c?.name ?? c?.Name ?? c?.description ?? c?.Description ?? '').trim()
      })).filter(c => c.id > 0 && c.name.length > 0);
      setCategoryList(cats);
      setCurrentCategoryIndex(0);
      if (cats.length > 0) {
        setQuote(p => ({
          ...p,
          optionsSection: {
            ...p.optionsSection,
            category: cats[0].name
          }
        }));
        await loadPartsByCategory(cats[0].id);
      }
    } catch {
      setCategoryList([]);
    }
  };
  const loadPartsByCategory = async categoryId => {
    try {
      const result = await dispatch(categoryApi.endpoints.getPartsByCategory.initiate({
        categoryId,
        projectId
      }, {
        subscribe: false
      })).unwrap();
      const parts = toArray(result).map((part, index) => ({
        id: toFiniteNumber(part?.id, index + 1),
        optionNumber: String(part?.partNumber ?? part?.optionNumber ?? `OPT-${index + 1}`),
        description: String(part?.name ?? part?.description ?? ''),
        listPrice: toFiniteNumber(part?.listPrice ?? part?.price, 0),
        quantity: 1,
        optionType: 'ADDL',
        discountMultiplier: toFiniteNumber(part?.discountMultiplier, 1),
        discountPrice: toFiniteNumber(part?.listPrice ?? part?.price, 0)
      }));
      setQuote(p => ({
        ...p,
        optionPickList: parts
      }));
    } catch {
      setQuote(p => ({
        ...p,
        optionPickList: []
      }));
    }
  };
  const prevCategory = async () => {
    if (categoryList.length === 0) return;
    const newIndex = currentCategoryIndex > 0 ? currentCategoryIndex - 1 : categoryList.length - 1;
    setCurrentCategoryIndex(newIndex);
    const cat = categoryList[newIndex];
    setQuote(p => ({
      ...p,
      optionsSection: {
        ...p.optionsSection,
        category: cat.name
      }
    }));
    await loadPartsByCategory(cat.id);
  };
  const nextCategory = async () => {
    if (categoryList.length === 0) return;
    const newIndex = currentCategoryIndex < categoryList.length - 1 ? currentCategoryIndex + 1 : 0;
    setCurrentCategoryIndex(newIndex);
    const cat = categoryList[newIndex];
    setQuote(p => ({
      ...p,
      optionsSection: {
        ...p.optionsSection,
        category: cat.name
      }
    }));
    await loadPartsByCategory(cat.id);
  };
  const handleCategoryChange = async catName => {
    setQuote(p => ({
      ...p,
      optionsSection: {
        ...p.optionsSection,
        category: catName
      }
    }));
    const found = categoryList.find(c => c.name === catName);
    if (found) {
      setCurrentCategoryIndex(categoryList.indexOf(found));
      await loadPartsByCategory(found.id);
    }
  };
  const handleCategoryTypeChange = async newType => {
    setIsSaveEnabled(true);
    setQuote(p => ({
      ...p,
      optionsSection: {
        ...p.optionsSection,
        categoryType: newType
      }
    }));
    const apiType = newType === 'HIDE AUTO PICKS' ? '1' : '2';
    await loadCategoriesByType(apiType);
  };
  const stationTypeBlurValidation = (field, value) => {
    const fieldStates = getStationFieldStates(quote.stationType);
    if (!fieldStates[field].isVisible || fieldStates[field].isDisabled) {
      return;
    }
    const stId = getNamedIdByValue(stationModels, quote.stationType.stationType);
    let min = null;
    let max = null;
    let message = '';
    if (field === 'sccrRating') {
      min = 35;
      max = 200;
      message = 'SCCR Rating must be between 35 and 200.';
    } else if (field === 'dischargePsi') {
      if (stId === 8) {
        min = 20;
        max = 100;
      } else if (stId === 9) {
        min = 20;
        max = 120;
      } else {
        min = 60;
        max = 150;
      }
      message = `Discharge PSI must be between ${min} and ${max} for this station type.`;
    } else if (field === 'wetWellDepthFt') {
      min = 5;
      max = 26;
      message = 'Wet Well Depth (Ft) must be between 5 and 26.';
    } else if (field === 'wetWellDepthIn') {
      min = 0;
      max = 11;
      message = 'Wet Well Depth (In) must be between 0 and 11.';
    } else if (field === 'vtPumpBottomClearance') {
      min = 4;
      max = 24;
      message = 'VT Pump Bottom Clearance must be between 4 and 24.';
    } else if (field === 'baseDimensionL' || field === 'baseDimensionW') {
      min = 24;
      message = 'Base dimensions must be at least 24 inches.';
    }
    if (min !== null && (value < min || max !== null && value > max)) {
      setValidationMessage(message);
      setShowValidationModal(true);
      const prev = prevStationValues[field];
      if (prev !== undefined) {
        setQuote(p => ({
          ...p,
          stationType: {
            ...p.stationType,
            [field]: prev
          }
        }));
      }
      return;
    }
    setPrevStationValues(p => ({
      ...p,
      [field]: value
    }));
  };
  const openEditOptionModal = index => {
    const option = quote.selectedOptionList[index];
    if (!option) return;
    setEditOptionIndex(index);
    setEditOptionData({
      partName: option.partName,
      description: option.partName
    });
    setShowEditOptionModal(true);
  };
  const saveEditOption = () => {
    if (editOptionIndex < 0) return;
    onFormChange(p => ({
      ...p,
      selectedOptionList: p.selectedOptionList.map((item, i) => i === editOptionIndex ? {
        ...item,
        partName: editOptionData.partName
      } : item)
    }));
    setShowEditOptionModal(false);
    setEditOptionIndex(-1);
  };
  const openCustomOptionModal = () => {
    setCustomOptionData({
      name: '',
      listPrice: '',
      description: '',
      partNumber: `CUSTOM-${Date.now()}`
    });
    setShowCustomOptionModal(true);
  };
  const saveCustomOption = () => {
    const price = toFiniteNumber(customOptionData.listPrice, 0);
    if (!customOptionData.name.trim() || price <= 0) return;
    const newOption = {
      id: Date.now(),
      optionNumber: customOptionData.partNumber,
      category: quote.optionsSection.category,
      partName: customOptionData.name.trim(),
      quantity: 1,
      listPrice: price,
      extendedPrice: price,
      base: false,
      addl: true
    };
    onFormChange(p => ({
      ...p,
      selectedOptionList: [...p.selectedOptionList, newOption]
    }));
    setShowCustomOptionModal(false);
  };
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [lookupsResult, usersResult, saleQuoteLookupsResult, stationTypeConfigResult] = await Promise.all([dispatch(lookupApi.endpoints.getLookups.initiate(undefined, {
          subscribe: false
        })).unwrap().catch(() => ({
          lookups: [],
          totalCount: 0
        })), dispatch(userApi.endpoints.getUsers.initiate(undefined, {
          subscribe: false
        })).unwrap().catch(() => ({
          users: [],
          totalCount: 0
        })), dispatch(lookupApi.endpoints.getSaleQuoteLookupData.initiate(undefined, {
          subscribe: false
        })).unwrap().catch(() => ({
          stationModel: [],
          configurationModel: [],
          primeSystemModel: []
        })), dispatch(lookupApi.endpoints.getStationTypeConfigs.initiate(undefined, {
          subscribe: false
        })).unwrap().catch(() => [])]);
        const lookups = Array.isArray(lookupsResult?.lookups) ? lookupsResult.lookups : [];
        const users = Array.isArray(usersResult?.users) ? usersResult.users : [];
        const saleQuoteLookups = saleQuoteLookupsResult ?? {};
        const configs = Array.isArray(stationTypeConfigResult) ? stationTypeConfigResult : [];
        const buckets = {};
        lookups.filter(lookup => lookup?.isActive !== false).forEach(lookup => {
          const lookupEntry = lookup;
          const lookupType = lookupEntry?.lookupType ?? lookupEntry?.LookupType;
          const rawType = lookupType?.typeName || lookupType?.TypeName || lookupType?.name || lookupType?.Name || lookupEntry?.typeName || lookupEntry?.TypeName || '';
          const normalizedType = normalizeLookupKey(rawType);
          if (!normalizedType) return;
          const displayValue = String(lookupEntry?.name ?? lookupEntry?.Name ?? lookupEntry?.description ?? lookupEntry?.Description ?? lookupEntry?.value ?? lookupEntry?.Value ?? '').trim();
          if (!displayValue) return;
          buckets[normalizedType] = buckets[normalizedType] || [];
          if (!buckets[normalizedType].includes(displayValue)) {
            buckets[normalizedType].push(displayValue);
          }
        });
        setLookupBuckets(buckets);
        const mappedUsers = users.map(user => `${user?.firstName ?? user?.FirstName ?? ''} ${user?.lastName ?? user?.LastName ?? ''}`.trim()).filter(Boolean);
        setUserNames(Array.from(new Set(mappedUsers)));
        const mappedStations = (saleQuoteLookups.stationModel || []).map(item => ({
          id: Number(item?.id ?? item?.Id ?? 0),
          name: String(item?.displayStationName ?? item?.DisplayStationName ?? item?.stationName ?? item?.StationName ?? item?.name ?? item?.Name ?? '').trim()
        })).filter(item => item.id > 0 && item.name.length > 0);
        const mappedConfigurations = (saleQuoteLookups.configurationModel || []).map(item => ({
          id: Number(item?.id ?? item?.Id ?? 0),
          name: String(item?.name ?? item?.Name ?? '').trim()
        })).filter(item => item.id > 0 && item.name.length > 0);
        const mappedPrimeSystems = (saleQuoteLookups.primeSystemModel || []).map(item => ({
          id: Number(item?.id ?? item?.Id ?? 0),
          name: String(item?.name ?? item?.Name ?? '').trim()
        })).filter(item => item.id > 0 && item.name.length > 0);
        setStationModels(mappedStations);
        setConfigurationModels(mappedConfigurations);
        setPrimeSystemModels(mappedPrimeSystems);
        setStationTypeConfigs(configs);
        try {
          const enclosureResult = await dispatch(categoryApi.endpoints.getAllEnclosurePricings.initiate(undefined, {
            subscribe: false
          })).unwrap().catch(() => []);
          const pricings = toArray(enclosureResult).map(item => ({
            length: toFiniteNumber(item?.enclosureLength ?? item?.length ?? item?.Length, 0),
            width: toFiniteNumber(item?.enclosureWidth ?? item?.width ?? item?.Width, 0)
          })).filter(p => p.length > 0 && p.width > 0);
          setEnclosurePricingList(pricings);
        } catch {
          setEnclosurePricingList([]);
        }
      } catch {
        setLookupBuckets({});
        setUserNames([]);
        setStationModels([]);
        setConfigurationModels([]);
        setPrimeSystemModels([]);
        setStationTypeConfigs([]);
      }
    };
    loadDropdownData();
  }, [dispatch]);
  useEffect(() => {
    const quoteId = Number(id);
    if (!quoteId) {
      return;
    }
    let isMounted = true;
    const loadQuoteDetail = async () => {
      try {
        const [quotePayload, costSummaryRows, lookupsResult] = await Promise.all([dispatch(salesQuoteApi.endpoints.getQuoteById.initiate(quoteId, {
          subscribe: false
        })).unwrap(), dispatch(costSummaryApi.endpoints.getCostSummariesBySaleQuote.initiate(quoteId, {
          subscribe: false
        })).unwrap().catch(() => []), dispatch(lookupApi.endpoints.getLookups.initiate(undefined, {
          subscribe: false
        })).unwrap().catch(() => ({
          lookups: [],
          totalCount: 0
        }))]);
        if (!isMounted) {
          return;
        }
        const costSummaryPayload = costSummaryRows.find(row => Number(row?.saleQuoteId ?? row?.SaleQuoteId) === quoteId) || firstItemOrObject(costSummaryRows);
        const statusLookup = buildProjectStatusLookup(Array.isArray(lookupsResult?.lookups) ? lookupsResult.lookups : []);
        const quotePayloadAny = quotePayload;
        const resolvedProjectId = Number(quotePayloadAny?.projectId ?? quotePayloadAny?.ProjectId ?? quotePayloadAny?.project?.id ?? quotePayloadAny?.project?.Id ?? quotePayloadAny?.Project?.id ?? quotePayloadAny?.Project?.Id ?? 0);
        let quotePayloadWithProject = quotePayloadAny;
        if (resolvedProjectId > 0) {
          setProjectId(resolvedProjectId);
          try {
            const projectPayload = await dispatch(projectApi.endpoints.getProjectById.initiate(resolvedProjectId, {
              subscribe: false
            })).unwrap();
            const mergedProject = {
              ...(quotePayloadAny?.project ?? quotePayloadAny?.Project ?? {}),
              ...(projectPayload ?? {})
            };
            quotePayloadWithProject = {
              ...quotePayloadAny,
              project: mergedProject
            };
          } catch {}
        }
        if (!isMounted) {
          return;
        }
        setQuote(previous => normalizeSalesQuoteDetail(quotePayloadWithProject, previous, statusLookup, costSummaryPayload));
      } catch {}
    };
    loadQuoteDetail();
    return () => {
      isMounted = false;
    };
  }, [dispatch, id]);
  useEffect(() => {
    if (stationModels.length === 0) return;
    const stId = getNamedIdByValue(stationModels, quote.stationType.stationType);
    if (stId <= 0) return;
    const apiType = quote.optionsSection.categoryType === 'HIDE AUTO PICKS' ? '1' : '2';
    loadCategoriesByType(apiType);
  }, [stationModels.length, quote.stationType.stationType, quote.stationType.configuration, quote.stationType.primeSystem, quote.optionsSection.categoryType]);
  const buildSelectOptions = (current, lookupTypeNames = [], fallback = []) => {
    const lookupValues = lookupTypeNames.flatMap(name => lookupBuckets[normalizeLookupKey(name)] || []);
    const allValues = [String(current), ...lookupValues, ...fallback.map(item => String(item))].filter(Boolean);
    return Array.from(new Set(allValues));
  };
  const getNamedIdByValue = (list, value) => {
    const normalizedValue = value.trim().toLowerCase();
    const matched = list.find(item => item.name.toLowerCase() === normalizedValue);
    return matched?.id ?? 0;
  };
  const getLookupIdByLabel = (lookupTypeNames, value) => {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return 0;
    for (const lookupTypeName of lookupTypeNames) {
      const bucketKey = normalizeLookupKey(lookupTypeName);
      const bucketValues = lookupBuckets[bucketKey] || [];
      const index = bucketValues.findIndex(item => item.trim().toLowerCase() === normalized);
      if (index >= 0) {
        return index + 1;
      }
    }
    return 0;
  };
  const getStationFieldStates = currentStationType => {
    const stationId = getNamedIdByValue(stationModels, currentStationType.stationType);
    const controlPanelorKitId = getLookupIdByLabel(['controlpanelorkit'], currentStationType.panelOrKit);
    const isStation10 = stationId === 10;
    const isKit272 = controlPanelorKitId === 272;
    const fallbackStates = {
      stationType: {
        isVisible: true,
        isDisabled: false
      },
      zones: {
        isVisible: !isStation10,
        isDisabled: false
      },
      configuration: {
        isVisible: !isStation10,
        isDisabled: false
      },
      primeSystem: {
        isVisible: !isStation10,
        isDisabled: false
      },
      panelOrKit: {
        isVisible: isStation10,
        isDisabled: false
      },
      type: {
        isVisible: isStation10 && !isKit272,
        isDisabled: false
      },
      vtTypeId: {
        isVisible: stationId === 2,
        isDisabled: false
      },
      dynamicInletPsi: {
        isVisible: !isKit272,
        isDisabled: false
      },
      feetOfLift: {
        isVisible: !isKit272,
        isDisabled: false
      },
      dischargePsi: {
        isVisible: !isKit272,
        isDisabled: false
      },
      dischargeFilterPresent: {
        isVisible: !isKit272,
        isDisabled: false
      },
      totalGpm: {
        isVisible: !isKit272,
        isDisabled: true
      },
      uvFilter: {
        isVisible: stationId === 9,
        isDisabled: false
      },
      siteVoltage: {
        isVisible: !isKit272,
        isDisabled: false
      },
      stationLocation: {
        isVisible: !isKit272,
        isDisabled: false
      },
      sccrRating: {
        isVisible: !isKit272,
        isDisabled: false
      },
      fccType: {
        isVisible: !isKit272,
        isDisabled: false
      },
      controlSystemPlatform: {
        isVisible: !isKit272,
        isDisabled: false
      },
      mainPanelLocation: {
        isVisible: !isKit272,
        isDisabled: false
      },
      flowMeterLocation: {
        isVisible: !isKit272,
        isDisabled: false
      },
      wetWellDepthFt: {
        isVisible: !isStation10,
        isDisabled: false
      },
      wetWellDepthIn: {
        isVisible: !isStation10,
        isDisabled: false
      },
      vtPumpBottomClearance: {
        isVisible: !isStation10,
        isDisabled: false
      },
      pumpColumnType: {
        isVisible: !isStation10,
        isDisabled: false
      },
      baseMaterial: {
        isVisible: !isStation10,
        isDisabled: false
      },
      stationColor: {
        isVisible: !isStation10,
        isDisabled: false
      },
      baseDimensionL: {
        isVisible: !isStation10,
        isDisabled: false
      },
      baseDimensionW: {
        isVisible: !isStation10,
        isDisabled: false
      }
    };
    const context = getStationTypeConfigContext(currentStationType);
    return Object.keys(STATION_TYPE_FIELD_LABELS).reduce((accumulator, fieldKey) => {
      const config = resolveStationTypeFieldConfig(stationTypeConfigs, context, fieldKey);
      const fallbackState = fallbackStates[fieldKey];
      accumulator[fieldKey] = {
        isConfigured: Boolean(config),
        isVisible: config ? !config.isHidden : fallbackState.isVisible,
        isDisabled: config ? config.isDisabled || fallbackState.isDisabled : fallbackState.isDisabled,
        defaultValue: config?.defaultValue ?? null,
        options: config?.options ?? []
      };
      return accumulator;
    }, {});
  };
  const buildConfiguredSelectOptions = (fieldState, current, lookupTypeNames = [], fallback = []) => {
    const fallbackOptions = buildSelectOptions(current, lookupTypeNames, fallback).map(option => ({
      value: String(option),
      label: String(option)
    }));
    const baseOptions = fieldState.isConfigured ? fieldState.options : fallbackOptions;
    const currentValue = String(current ?? '').trim();
    if (!currentValue) {
      return dedupeSelectOptions(baseOptions);
    }
    const hasCurrentValue = baseOptions.some(option => normalizeComparableValue(option.value) === normalizeComparableValue(currentValue));
    return dedupeSelectOptions(hasCurrentValue ? baseOptions : [{
      value: currentValue,
      label: currentValue
    }, ...baseOptions]);
  };
  useEffect(() => {
    if (stationTypeConfigs.length === 0) {
      return;
    }
    setQuote(previous => {
      let nextStationType = previous.stationType;
      let changed = false;
      let pass = 0;
      while (pass < 5) {
        pass += 1;
        const fieldStates = getStationFieldStates(nextStationType);
        let changedThisPass = false;
        Object.keys(STATION_TYPE_FIELD_LABELS).forEach(fieldKey => {
          const fieldState = fieldStates[fieldKey];
          const currentValue = nextStationType[fieldKey];
          const configuredOptionValues = fieldState.options.map(option => normalizeComparableValue(option.value));
          const hasConfiguredOptions = configuredOptionValues.length > 0;
          const currentValueStillAllowed = hasConfiguredOptions ? configuredOptionValues.includes(normalizeComparableValue(currentValue)) : true;
          if (fieldState.isConfigured && !fieldState.isVisible) {
            const emptyValue = getEmptyStationTypeFieldValue(fieldKey);
            if (String(currentValue) !== String(emptyValue)) {
              nextStationType = {
                ...nextStationType,
                [fieldKey]: emptyValue
              };
              changed = true;
              changedThisPass = true;
            }
            return;
          }
          if (fieldState.defaultValue) {
            const currentValueIsEmpty = isStationTypeFieldEmpty(fieldKey, currentValue);
            if (!currentValueIsEmpty && currentValueStillAllowed) {
              return;
            }
            const nextValue = coerceStationTypeFieldValue(fieldKey, fieldState.defaultValue);
            if (String(currentValue) !== String(nextValue)) {
              nextStationType = {
                ...nextStationType,
                [fieldKey]: nextValue
              };
              changed = true;
              changedThisPass = true;
            }
            return;
          }
          if (fieldState.isConfigured && (!currentValueStillAllowed || fieldState.isDisabled)) {
            const emptyValue = getEmptyStationTypeFieldValue(fieldKey);
            if (!isStationTypeFieldEmpty(fieldKey, currentValue) && String(currentValue) !== String(emptyValue)) {
              nextStationType = {
                ...nextStationType,
                [fieldKey]: emptyValue
              };
              changed = true;
              changedThisPass = true;
            }
          }
        });
        if (!changedThisPass) {
          break;
        }
      }
      return changed ? {
        ...previous,
        stationType: nextStationType
      } : previous;
    });
  }, [lookupBuckets, stationModels, stationTypeConfigs, quote.stationType.configuration, quote.stationType.panelOrKit, quote.stationType.stationType, quote.stationType.type, quote.stationType.vtTypeId]);
  const addPumpConfigurationItem = () => {
    setQuote(previous => {
      const nextId = previous.pumpConfigurations.length ? Math.max(...previous.pumpConfigurations.map(item => item.id)) + 1 : 1;
      return {
        ...previous,
        pumpConfigurations: [...previous.pumpConfigurations, {
          id: nextId,
          qty: 1,
          pumpDescription: previous.pumpConfigurations[0]?.pumpDescription || '7IMH-7',
          motorHP: previous.pumpConfigurations[0]?.motorHP || '15',
          type: previous.pumpConfigurations[0]?.type || 'Vertical Turbine',
          gpm: 0,
          vfd: 'No',
          xl: 'No',
          ss: 'No',
          ebv: 'No',
          zone: previous.stationType.zones || 1
        }]
      };
    });
  };
  const normalizePumpFromApi = (input, fallback, id) => {
    const yesNoFromValue = (value, defaultValue = 'No') => {
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['yes', 'y', 'true', '1'].includes(normalized)) return 'Yes';
        if (['no', 'n', 'false', '0'].includes(normalized)) return 'No';
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      if (typeof value === 'number') {
        return value === 1 ? 'Yes' : 'No';
      }
      return defaultValue;
    };
    const asNumber = (value, fallbackValue) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallbackValue;
    };
    const asText = (value, fallbackValue) => {
      if (typeof value === 'string' && value.trim().length > 0) return value;
      if (typeof value === 'number') return String(value);
      return fallbackValue;
    };
    return {
      id,
      qty: asNumber(input.quantity ?? input.qty, fallback.qty),
      pumpDescription: asText(input.pumpDescription ?? input.pumpDescId ?? input.pumpDesc, fallback.pumpDescription),
      motorHP: asText(input.motorHP ?? input.motorHPId, fallback.motorHP),
      type: asText(input.type ?? input.pumpType ?? input.pumpTypeId, fallback.type),
      gpm: asNumber(input.gpm, fallback.gpm),
      vfd: yesNoFromValue(input.vfd ?? input.vfdId, fallback.vfd === 'Yes' ? 'Yes' : 'No'),
      xl: yesNoFromValue(input.xl, fallback.xl),
      ss: yesNoFromValue(input.ss, fallback.ss),
      ebv: yesNoFromValue(input.ebv, fallback.ebv),
      zone: asNumber(input.zone, fallback.zone ?? 1)
    };
  };
  const collectAutoPickValidationErrors = currentQuote => {
    const errors = [];
    const isBlank = value => {
      if (typeof value === 'string') return value.trim().length === 0;
      if (typeof value === 'number') return Number.isNaN(value) || value === 0;
      return value === null || value === undefined;
    };
    const stationId = getNamedIdByValue(stationModels, currentQuote.stationType.stationType);
    const configurationId = getNamedIdByValue(configurationModels, currentQuote.stationType.configuration);
    const controlPanelorKitId = getLookupIdByLabel(['controlpanelorkit'], currentQuote.stationType.panelOrKit);
    const controlPanelorKitTypeId = getLookupIdByLabel(['controlpanelorkittype'], currentQuote.stationType.type);
    const fieldStates = getStationFieldStates(currentQuote.stationType);
    const excludedControlPanelorKitIds = [271, 272, 281, 268, 269, 270];
    const bypassValidation = controlPanelorKitId === 272;
    if (!stationId && fieldStates.stationType.isVisible && !fieldStates.stationType.isDisabled) {
      errors.push('Please select Station type.');
    }
    if (fieldStates.dynamicInletPsi.isVisible && !fieldStates.dynamicInletPsi.isDisabled && isBlank(currentQuote.stationType.dynamicInletPsi)) {
      errors.push('Dynamic Inlet PSI is required.');
    }
    if (fieldStates.feetOfLift.isVisible && !fieldStates.feetOfLift.isDisabled && isBlank(currentQuote.stationType.feetOfLift)) {
      errors.push('Feet of Lift is required.');
    }
    if ([5, 6, 7].includes(stationId)) {
      if (configurationId === 1 && fieldStates.dynamicInletPsi.isVisible && !fieldStates.dynamicInletPsi.isDisabled && isBlank(currentQuote.stationType.dynamicInletPsi)) {
        errors.push('Dynamic Intel PSI is required');
      }
      if (configurationId === 2 && fieldStates.feetOfLift.isVisible && !fieldStates.feetOfLift.isDisabled && isBlank(currentQuote.stationType.feetOfLift)) {
        errors.push('Feet of Lift is required');
      }
    }
    if (!currentQuote.pumpConfigurations.length && !bypassValidation) {
      errors.push('Please add atleast one pump.');
    } else {
      currentQuote.pumpConfigurations.forEach((pump, index) => {
        const row = index + 1;
        if (!pump.qty || pump.qty <= 0) errors.push(`Pump row ${row}: Quantity is required.`);
        if (isBlank(pump.pumpDescription)) errors.push(`Pump row ${row}: Pump Description is required.`);
        if (isBlank(pump.motorHP)) errors.push(`Pump row ${row}: Motor HP is required.`);
        if (isBlank(pump.type)) errors.push(`Pump row ${row}: Type is required.`);
        if (!pump.gpm || pump.gpm <= 0) errors.push(`Pump row ${row}: GPM is required.`);
      });
    }
    if (fieldStates.sccrRating.isVisible && !fieldStates.sccrRating.isDisabled && (!isBlank(currentQuote.stationType.sccrRating) || !excludedControlPanelorKitIds.includes(controlPanelorKitId))) {
      if (isBlank(currentQuote.stationType.sccrRating) && !excludedControlPanelorKitIds.includes(controlPanelorKitId)) {
        errors.push('A SCCR Rating is required');
      }
    }
    if (fieldStates.siteVoltage.isVisible && !fieldStates.siteVoltage.isDisabled && isBlank(currentQuote.stationType.siteVoltage) && !excludedControlPanelorKitIds.includes(controlPanelorKitId)) {
      errors.push('A Site Voltage is required');
    }
    if (fieldStates.dischargePsi.isVisible && !fieldStates.dischargePsi.isDisabled && isBlank(currentQuote.stationType.dischargePsi) && controlPanelorKitId !== 272) {
      errors.push('A Discharge PSI is required');
    }
    if (fieldStates.baseDimensionL.isVisible && !fieldStates.baseDimensionL.isDisabled && stationId !== 10 && isBlank(currentQuote.stationType.baseDimensionL)) {
      errors.push('A Base Length is required');
    }
    if (fieldStates.controlSystemPlatform.isVisible && !fieldStates.controlSystemPlatform.isDisabled && isBlank(currentQuote.stationType.controlSystemPlatform) && ![271, 272].includes(controlPanelorKitId)) {
      errors.push('Control System Platform is required');
    }
    if (fieldStates.stationLocation.isVisible && !fieldStates.stationLocation.isDisabled && isBlank(currentQuote.stationType.stationLocation) && !excludedControlPanelorKitIds.includes(controlPanelorKitId)) {
      errors.push('Site Location is required');
    }
    if (fieldStates.baseMaterial.isVisible && !fieldStates.baseMaterial.isDisabled && stationId !== 10 && isBlank(currentQuote.stationType.baseMaterial)) {
      errors.push('Base Material is required');
    }
    if (fieldStates.panelOrKit.isVisible && !fieldStates.panelOrKit.isDisabled && stationId === 10 && isBlank(currentQuote.stationType.panelOrKit)) {
      errors.push('Panel or kit is required');
    }
    if (fieldStates.type.isVisible && !fieldStates.type.isDisabled && stationId === 10 && controlPanelorKitId !== 272 && isBlank(currentQuote.stationType.type)) {
      errors.push('Panel or kit Type is required');
    }
    if (fieldStates.dynamicInletPsi.isVisible && !fieldStates.dynamicInletPsi.isDisabled && stationId === 10 && controlPanelorKitId !== 272 && [274, 277].includes(controlPanelorKitTypeId) && isBlank(currentQuote.stationType.dynamicInletPsi)) {
      errors.push('Dynamic Inlet PSI is required');
    }
    const stationTypeLower = currentQuote.stationType.stationType.toLowerCase();
    const isVtStyleStation = stationTypeLower.includes('vt') || stationTypeLower.includes('vertical');
    if (isVtStyleStation || [1, 2].includes(stationId)) {
      if (fieldStates.wetWellDepthFt.isVisible && !fieldStates.wetWellDepthFt.isDisabled && isBlank(currentQuote.stationType.wetWellDepthFt)) {
        errors.push('Wet Well depth Ft is required.');
      }
      if (fieldStates.wetWellDepthIn.isVisible && !fieldStates.wetWellDepthIn.isDisabled && isBlank(currentQuote.stationType.wetWellDepthIn)) {
        errors.push('Wet Well depth In is required.');
      }
      if (fieldStates.vtPumpBottomClearance.isVisible && !fieldStates.vtPumpBottomClearance.isDisabled && isBlank(currentQuote.stationType.vtPumpBottomClearance)) {
        errors.push('A VT Pump Bottom Clearance is required.');
      }
    }
    const dedupedErrors = Array.from(new Set(errors));
    const totalGpm = currentQuote.pumpConfigurations.reduce((sum, pump) => {
      const qty = Number(pump.qty) || 0;
      const gpm = Number(pump.gpm) || 0;
      return sum + qty * gpm;
    }, 0);
    return {
      errors: dedupedErrors,
      totalGpm
    };
  };
  const loadAutoPick = async () => {
    setAutoPickErrors([]);
    const globalCheck = advancedRules.evaluate(quote);
    if (!globalCheck.isValid || globalCheck.updates.length) {
      setAutoPickErrors(globalCheck.errors.map(error => error.message).concat(globalCheck.updates.length ? ['Automatic rules updated the configuration. Review and run Auto Pick again.'] : []));
      if (globalCheck.updates.length) setQuote(globalCheck.quote);
      return;
    }
    const precheck = collectAutoPickValidationErrors(quote);
    if (precheck.errors.length) {
      setAutoPickErrors(precheck.errors);
      return;
    }
    const quoteForAutoPick = {
      ...quote,
      stationType: {
        ...quote.stationType,
        totalGpm: precheck.totalGpm
      }
    };
    setQuote(quoteForAutoPick);
    const stationId = getNamedIdByValue(stationModels, quoteForAutoPick.stationType.stationType);
    const configurationId = getNamedIdByValue(configurationModels, quoteForAutoPick.stationType.configuration);
    const primeSystemId = getNamedIdByValue(primeSystemModels, quoteForAutoPick.stationType.primeSystem);
    const controlPanelorKitId = getLookupIdByLabel(['controlpanelorkit'], quoteForAutoPick.stationType.panelOrKit);
    const controlPanelorKitTypeId = getLookupIdByLabel(['controlpanelorkittype'], quoteForAutoPick.stationType.type);
    const eleSiteVoltageId = getLookupIdByLabel(['sitevoltage'], quoteForAutoPick.stationType.siteVoltage);
    const siteLocationId = getLookupIdByLabel(['stationlocation'], quoteForAutoPick.stationType.stationLocation);
    const fccTypeId = getLookupIdByLabel(['fcctype'], quoteForAutoPick.stationType.fccType);
    const controlSystemPlatFormId = getLookupIdByLabel(['controlsystem'], quoteForAutoPick.stationType.controlSystemPlatform);
    const mainPanelLocationId = getLookupIdByLabel(['mainpanel'], quoteForAutoPick.stationType.mainPanelLocation);
    const flowMeterLocationId = getLookupIdByLabel(['flowmeter'], quoteForAutoPick.stationType.flowMeterLocation);
    const pumpColumnTypeId = getLookupIdByLabel(['pumpcloumntype', 'pumpcolumntype'], quoteForAutoPick.stationType.pumpColumnType);
    const baseMaterialId = getLookupIdByLabel(['basemeterial', 'basematerial'], quoteForAutoPick.stationType.baseMaterial);
    const stationColorId = getLookupIdByLabel(['stationcolor'], quoteForAutoPick.stationType.stationColor);
    const dischargeFilterPercent = quoteForAutoPick.stationType.dischargeFilterPresent === 'YES' ? 1 : 0;
    setIsAutoPicking(true);
    try {
      const payload = {
        stationTypes: {
          stationId,
          configurationId,
          primeSystemId,
          controlPanelorKitId,
          controlPanelorKitTypeId,
          stationType: quoteForAutoPick.stationType.stationType,
          zones: quoteForAutoPick.stationType.zones,
          configuration: quoteForAutoPick.stationType.configuration,
          primeSystem: quoteForAutoPick.stationType.primeSystem,
          panelOrKit: quoteForAutoPick.stationType.panelOrKit,
          type: quoteForAutoPick.stationType.type,
          dynamicInletPsi: quoteForAutoPick.stationType.dynamicInletPsi,
          dynamicIntelPsi: quoteForAutoPick.stationType.dynamicInletPsi,
          feetOfLift: quoteForAutoPick.stationType.feetOfLift,
          dischargePsi: quoteForAutoPick.stationType.dischargePsi,
          disChargePsi: quoteForAutoPick.stationType.dischargePsi,
          dischargeFilterPresent: quoteForAutoPick.stationType.dischargeFilterPresent,
          dischargeFilterPercent,
          totalGpm: quoteForAutoPick.stationType.totalGpm,
          siteVoltage: quoteForAutoPick.stationType.siteVoltage,
          eleSiteVoltageId,
          eleSiteVoltage: eleSiteVoltageId,
          stationLocation: quoteForAutoPick.stationType.stationLocation,
          siteLocationId,
          siteLocation: siteLocationId,
          sccrRating: quoteForAutoPick.stationType.sccrRating,
          sccrrating: quoteForAutoPick.stationType.sccrRating,
          fccType: quoteForAutoPick.stationType.fccType,
          fccTypeId,
          controlSystemPlatform: quoteForAutoPick.stationType.controlSystemPlatform,
          controlSystemPlatFormId,
          mainPanelLocation: quoteForAutoPick.stationType.mainPanelLocation,
          mainPanelLocationId,
          flowMeterLocation: quoteForAutoPick.stationType.flowMeterLocation,
          flowMeterLocationId,
          wetWellDepthFt: quoteForAutoPick.stationType.wetWellDepthFt,
          wetWellDepthIn: quoteForAutoPick.stationType.wetWellDepthIn,
          wetWellDepthInh: quoteForAutoPick.stationType.wetWellDepthIn,
          vtPumpBottomClearance: quoteForAutoPick.stationType.vtPumpBottomClearance,
          vtpumpBottomClearance: quoteForAutoPick.stationType.vtPumpBottomClearance,
          pumpColumnType: quoteForAutoPick.stationType.pumpColumnType,
          pumpColumnTypeId,
          baseMaterial: quoteForAutoPick.stationType.baseMaterial,
          baseMaterialId,
          stationColor: quoteForAutoPick.stationType.stationColor,
          stationColorId,
          baseDimensionL: quoteForAutoPick.stationType.baseDimensionL,
          baseDimensionLen: quoteForAutoPick.stationType.baseDimensionL,
          baseDimensionW: quoteForAutoPick.stationType.baseDimensionW,
          baseDimensionWht: quoteForAutoPick.stationType.baseDimensionW,
          quoteType: quoteForAutoPick.costSummary.warrantyType === 'Domestic' ? 1 : 2,
          autoPickFlag: true
        },
        pumpConfigurations: jockeyFirst(quoteForAutoPick.pumpConfigurations).map(pump => ({
          quantity: pump.qty,
          pumpDescription: pump.pumpDescription,
          motorHP: pump.motorHP,
          pumpType: pump.type,
          gpm: pump.gpm,
          vfd: pump.vfd,
          xl: pump.xl,
          ss: pump.ss,
          ebv: pump.ebv,
          zone: pump.zone
        })),
        selectedOptions: quoteForAutoPick.selectedOptionList.map(option => ({
          optionNumber: option.optionNumber,
          category: option.category,
          partName: option.partName,
          quantity: option.quantity,
          listPrice: option.listPrice,
          extendedPrice: option.extendedPrice,
          base: option.base,
          addl: option.addl
        }))
      };
      const response = await dispatch(salesQuoteApi.endpoints.autopickQuote.initiate(payload)).unwrap();
      const autopickResponse = response?.data ?? response;
      const responseErrors = Array.isArray(autopickResponse?.errors) ? autopickResponse.errors.filter(Boolean) : [];
      if (responseErrors.length) {
        setAutoPickErrors(responseErrors);
      }
      setAutoPickFlag(true);
      setAutoPickCheckForWarning(true);
      if (Array.isArray(autopickResponse?.pumpConfigurations) && autopickResponse.pumpConfigurations.length) {
        setQuote(previous => {
          const updatedPumps = autopickResponse.pumpConfigurations.map((item, index) => {
            const fallback = previous.pumpConfigurations[index] || previous.pumpConfigurations[0];
            return normalizePumpFromApi(item, fallback || {
              id: index + 1,
              qty: 1,
              pumpDescription: '7IMH-7',
              motorHP: '15',
              type: 'Vertical Turbine',
              gpm: 0,
              vfd: 'No',
              xl: 'No',
              ss: 'No',
              ebv: 'No',
              zone: 1
            }, index + 1);
          });
          return {
            ...previous,
            pumpConfigurations: updatedPumps
          };
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Auto Pick failed. Please try again.';
      setAutoPickErrors([message]);
    } finally {
      setIsAutoPicking(false);
    }
  };
  const renderSelect = (value, options, onChange, disabled = false) => {
    const currentValue = String(value);
    const normalizedOptions = options.map(option => typeof option === 'object' && option !== null && 'value' in option ? {
      value: String(option.value),
      label: option.label ?? String(option.value),
      disabled: option.disabled ?? false
    } : {
      value: String(option),
      label: String(option),
      disabled: false
    });
    return <Form.Select value={currentValue} onChange={event => onChange(event.target.value)} disabled={disabled}>
        {normalizedOptions.map(option => <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>)}
      </Form.Select>;
  };
  const stationFieldStates = getStationFieldStates(quote.stationType);
  const isAdmin = true;
  const costSummary = {
    ...quote.costSummary,
    steelAdj: quote.costSummary.steelAdjAmount,
    outsideCommissionTotal: quote.costSummary.outsideCommissionAmount,
    rebates: quote.costSummary.rebatesAmount,
    otherMisc: quote.costSummary.otherInstallationMisc,
    setupMultiplier: quote.costSummary.setupMultiplierPercent,
    setupTotalValue: quote.costSummary.setupMultiplierAmount
  };
  const updateCostSummary = (field, value) => {
    const fieldMap = {
      steelAdj: 'steelAdjAmount',
      outsideCommissionTotal: 'outsideCommissionAmount',
      rebates: 'rebatesAmount',
      otherMisc: 'otherInstallationMisc',
      setupMultiplier: 'setupMultiplierPercent',
      setupTotalValue: 'setupMultiplierAmount'
    };
    const percentageFields = new Set(['steelAdjPercent', 'outsideCommissionPercent', 'rebatesPercent', 'setupMultiplierPercent']);
    const monetaryCappedFields = new Set(['freight', 'overseasShipment', 'setupAllowance', 'startupAllowance', 'craneRental', 'inspectionFees', 'otherInstallationMisc', 'taxes', 'steelAdjAmount', 'outsideCommissionAmount', 'rebatesAmount', 'setupMultiplierAmount']);
    const targetField = fieldMap[field] ?? field;
    let capped = value;
    if (percentageFields.has(targetField)) {
      capped = Math.min(capped, 99);
    } else if (monetaryCappedFields.has(targetField)) {
      capped = Math.min(capped, 999999);
    }
    setIsSaveEnabled(true);
    setQuote(previous => ({
      ...previous,
      costSummary: {
        ...previous.costSummary,
        [targetField]: capped
      }
    }));
  };
  return <div>
      <Row className="mb-3">
        <Col>
          <h4>Price Matrix</h4>
          <p className="text-muted mb-0">
            Quote: {quote.quotationNumber} | Project: {quote.generalInfo.projectName} | ID: {id ?? quote.id}
          </p>
        </Col>
        <Col className="text-end">
          <Button variant="outline-secondary" className="me-2" onClick={() => navigate('/sales/quotes')}>
            <i className="bi bi-arrow-left me-2"></i>
            Back to Sales Quotes
          </Button>
          {quote.id > 0 && <Button variant="outline-primary" onClick={getSalesQuoteVersions}>
              <i className="bi bi-clock-history me-2"></i>
              Versions
            </Button>}
        </Col>
      </Row>

      <Accordion activeKey={activeKey} alwaysOpen onSelect={keys => setActiveKey(keys)}>
        <Accordion.Item eventKey="0" className="mb-2">
          <Accordion.Header><span className="mx-auto text-px18"><strong>GENERAL INFORMATION</strong></span></Accordion.Header>
          <Accordion.Body>
            <Card className="mb-3">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check inline type="radio" label="Domestic" name="quoteType" checked={quote.costSummary.warrantyType === 'Domestic'} onChange={() => onFormChange(previous => ({
                      ...previous,
                      costSummary: {
                        ...previous.costSummary,
                        warrantyType: 'Domestic'
                      }
                    }))} />
                      <Form.Check inline type="radio" label="International" name="quoteType" checked={quote.costSummary.warrantyType === 'International'} onChange={() => onFormChange(previous => ({
                      ...previous,
                      costSummary: {
                        ...previous.costSummary,
                        warrantyType: 'International'
                      }
                    }))} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Quote No</Form.Label>
                      <Form.Control type="text" value={quote.quotationNumber} readOnly />
                    </Form.Group>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col md={4}>
                    <p className="mb-1"><strong>Date:</strong> {quote.generalInfo.date}</p>
                    <p className="mb-1"><strong>Project Name:</strong> {quote.generalInfo.projectName}</p>
                    <p className="mb-1"><strong>Project Location:</strong> {quote.generalInfo.projectLocation}</p>
                    <p className="mb-1"><strong>Contact Name:</strong> {quote.generalInfo.contactName}</p>
                  </Col>
                  <Col md={4}>
                    <p className="mb-1"><strong>Contact Company:</strong> {quote.generalInfo.contactCompany}</p>
                    <p className="mb-1"><strong>Contact Number:</strong> {quote.generalInfo.contactNumber}</p>
                    <p className="mb-1"><strong>Contact Email:</strong> {quote.generalInfo.contactEmail}</p>
                  </Col>
                  <Col md={4}>
                    <p className="mb-1"><strong>Contact Address:</strong> {quote.generalInfo.contactAddress}</p>
                    <p className="mb-1"><strong>Contact City:</strong> {quote.generalInfo.contactCity}</p>
                    <p className="mb-1"><strong>Contact State:</strong> {quote.generalInfo.contactState}</p>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>SALES REP</strong></Form.Label>
                      {renderSelect(quote.generalInfo.salesRep, buildSelectOptions(quote.generalInfo.salesRep, [], userNames.length ? userNames : ['John Sales', 'Mary Sales']), value => onFormChange(previous => ({
                      ...previous,
                      generalInfo: {
                        ...previous.generalInfo,
                        salesRep: value
                      }
                    })))}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>QUOTED BY</strong></Form.Label>
                      {renderSelect(quote.generalInfo.quotedBy, buildSelectOptions(quote.generalInfo.quotedBy, [], userNames.length ? userNames : ['Admin User', 'Estimator User']), value => onFormChange(previous => ({
                      ...previous,
                      generalInfo: {
                        ...previous.generalInfo,
                        quotedBy: value
                      }
                    })))}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1" className="mb-2">
          <Accordion.Header><span className="mx-auto text-px18"><strong>STATION TYPE</strong></span></Accordion.Header>
          <Accordion.Body>
            <fieldset disabled={quote.isProposalGenerated}>
            {(() => {
              const stId = getNamedIdByValue(stationModels, quote.stationType.stationType);
              const kitId = getLookupIdByLabel(['controlpanelorkit'], quote.stationType.panelOrKit);
              const isStation10 = stId === 10;
              const cpDocLabel = {
                267: 'CP Documentation',
                268: '66C Documentation',
                269: '65C Documentation',
                270: '64C Documentation',
                271: '7A Documentation',
                272: 'GPIO Documentation',
                281: '68C Documentation'
              };
              const stationTypeHyperlinks = resolveStationTypeHyperlinks(stationTypeConfigs, getStationTypeConfigContext(quote.stationType));
              const showStationInlet = stationFieldStates.dynamicInletPsi.isVisible || stationFieldStates.feetOfLift.isVisible;
              const showOutputPerformance = stationFieldStates.dischargePsi.isVisible || stationFieldStates.dischargeFilterPresent.isVisible || stationFieldStates.totalGpm.isVisible;
              const showElectrical = stationFieldStates.siteVoltage.isVisible || stationFieldStates.stationLocation.isVisible || stationFieldStates.sccrRating.isVisible || stationFieldStates.fccType.isVisible || stationFieldStates.controlSystemPlatform.isVisible || stationFieldStates.mainPanelLocation.isVisible || stationFieldStates.flowMeterLocation.isVisible;
              const showMechanical = stationFieldStates.wetWellDepthFt.isVisible || stationFieldStates.wetWellDepthIn.isVisible || stationFieldStates.vtPumpBottomClearance.isVisible || stationFieldStates.pumpColumnType.isVisible || stationFieldStates.baseMaterial.isVisible || stationFieldStates.stationColor.isVisible || stationFieldStates.baseDimensionL.isVisible || stationFieldStates.baseDimensionW.isVisible;
              const stationTypeColumnSize = stationFieldStates.zones.isVisible ? 9 : 12;
              return <Row>
                  <Col md={6}>
                    <Card className="mb-3">
                      <Card.Body>
                        <h6>Station Model</h6>
                        <Row>
                          {stationFieldStates.stationType.isVisible && <Col md={stationTypeColumnSize}>
                              <Form.Group className="mb-3">
                                <Form.Label>Station Type</Form.Label>
                                {renderSelect(quote.stationType.stationType, buildConfiguredSelectOptions(stationFieldStates.stationType, quote.stationType.stationType, ['stationmodel'], []), value => handleStationTypeChange(value), stationFieldStates.stationType.isDisabled)}
                              </Form.Group>
                            </Col>}

                          {stationFieldStates.zones.isVisible && <Col md={3}>
                              <Form.Group className="mb-3">
                                <Form.Label>Zones</Form.Label>
                                {renderSelect(quote.stationType.zones, buildConfiguredSelectOptions(stationFieldStates.zones, quote.stationType.zones, [], [1, 2]), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                zones: Number(value)
                              }
                            })), stationFieldStates.zones.isDisabled)}
                              </Form.Group>
                            </Col>}

                          {stationFieldStates.configuration.isVisible && <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Configuration</Form.Label>
                                {renderSelect(quote.stationType.configuration, buildConfiguredSelectOptions(stationFieldStates.configuration, quote.stationType.configuration, ['configurationmodel'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                configuration: value
                              }
                            })), stationFieldStates.configuration.isDisabled)}
                              </Form.Group>
                            </Col>}

                          {stationFieldStates.primeSystem.isVisible && <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Prime System</Form.Label>
                                {renderSelect(quote.stationType.primeSystem, buildConfiguredSelectOptions(stationFieldStates.primeSystem, quote.stationType.primeSystem, ['primesystemmodel'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                primeSystem: value
                              }
                            })), stationFieldStates.primeSystem.isDisabled)}
                              </Form.Group>
                            </Col>}

                          {stationFieldStates.panelOrKit.isVisible && <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Panel or Kit <span className="text-danger">*</span></Form.Label>
                                {renderSelect(quote.stationType.panelOrKit, buildConfiguredSelectOptions(stationFieldStates.panelOrKit, quote.stationType.panelOrKit, ['controlpanelorkit'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                panelOrKit: value
                              }
                            })), stationFieldStates.panelOrKit.isDisabled)}
                              </Form.Group>
                            </Col>}

                          {stationFieldStates.type.isVisible && <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Type <span className="text-danger">*</span></Form.Label>
                                {renderSelect(quote.stationType.type, buildConfiguredSelectOptions(stationFieldStates.type, quote.stationType.type, ['controlpanelorkittype'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                type: value
                              }
                            })), stationFieldStates.type.isDisabled)}
                              </Form.Group>
                            </Col>}

                          {stationFieldStates.vtTypeId.isVisible && <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>VT Type</Form.Label>
                                {renderSelect(quote.stationType.vtTypeId, buildConfiguredSelectOptions(stationFieldStates.vtTypeId, quote.stationType.vtTypeId, ['vtstandard', 'vttype'], []), value => handleVtTypeChange(value), stationFieldStates.vtTypeId.isDisabled)}
                              </Form.Group>
                            </Col>}
                        </Row>

                        {stationTypeHyperlinks.length > 0 ? stationTypeHyperlinks.map(link => <p className="mb-0 small" key={`${link.label}-${link.href}`}><a href={link.href} target="_blank" rel="noopener noreferrer"><i className="bi bi-link-45deg me-1"></i>{link.label}</a></p>) : <>
                          {stId === 8 && <p className="mb-0 small"><a href="#" onClick={e => e.preventDefault()}><i className="bi bi-file-earmark-pdf me-1"></i>SH Micro Documentation</a></p>}
                          {stId === 9 && <p className="mb-0 small"><a href="#" onClick={e => e.preventDefault()}><i className="bi bi-file-earmark-pdf me-1"></i>SH80 Documentation</a></p>}
                          {isStation10 && cpDocLabel[kitId] && <p className="mb-0 small"><a href="#" onClick={e => e.preventDefault()}><i className="bi bi-file-earmark-pdf me-1"></i>{cpDocLabel[kitId]}</a></p>}
                        </>}
                      </Card.Body>
                    </Card>

                    {showStationInlet && <Card className="mb-3">
                        <Card.Body>
                          <h6>Station Inlet</h6>
                          <Row>
                            {stationFieldStates.dynamicInletPsi.isVisible && <Col md={stationFieldStates.feetOfLift.isVisible ? 6 : 12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Dynamic Inlet PSI <span className="text-danger">*</span></Form.Label>
                                  <Form.Control type="number" value={quote.stationType.dynamicInletPsi} disabled={stationFieldStates.dynamicInletPsi.isDisabled} onChange={e => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                dynamicInletPsi: Number(e.target.value)
                              }
                            }))} />
                                </Form.Group>
                              </Col>}
                            {stationFieldStates.feetOfLift.isVisible && <Col md={stationFieldStates.dynamicInletPsi.isVisible ? 6 : 12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Feet of Lift <span className="text-danger">*</span></Form.Label>
                                  <Form.Control type="number" value={quote.stationType.feetOfLift} disabled={stationFieldStates.feetOfLift.isDisabled} onChange={e => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                feetOfLift: Number(e.target.value)
                              }
                            }))} />
                                </Form.Group>
                              </Col>}
                          </Row>
                        </Card.Body>
                      </Card>}

                    {showOutputPerformance && <Card className="mb-3">
                        <Card.Body>
                          <h6>Output Performance</h6>
                          <Row>
                            {stationFieldStates.dischargePsi.isVisible && <Col md={4}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Discharge PSI <span className="text-danger">*</span></Form.Label>
                                  <Form.Control type="number" value={quote.stationType.dischargePsi} disabled={stationFieldStates.dischargePsi.isDisabled} onChange={e => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                dischargePsi: Number(e.target.value)
                              }
                            }))} onBlur={e => stationTypeBlurValidation('dischargePsi', Number(e.target.value))} />
                                </Form.Group>
                              </Col>}
                            {stationFieldStates.dischargeFilterPresent.isVisible && <Col md={4}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Discharge Filter Present</Form.Label>
                                  {renderSelect(quote.stationType.dischargeFilterPresent, buildConfiguredSelectOptions(stationFieldStates.dischargeFilterPresent, quote.stationType.dischargeFilterPresent, [], ['YES', 'NO']), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                dischargeFilterPresent: value
                              }
                            })), stationFieldStates.dischargeFilterPresent.isDisabled)}
                                </Form.Group>
                              </Col>}
                            {stationFieldStates.totalGpm.isVisible && <Col md={4}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Total GPM</Form.Label>
                                  <Form.Control type="number" value={quote.stationType.totalGpm} readOnly disabled={stationFieldStates.totalGpm.isDisabled} />
                                </Form.Group>
                              </Col>}
                          </Row>
                        </Card.Body>
                      </Card>}

                    {stationFieldStates.uvFilter.isVisible && <Card className="mb-3">
                        <Card.Body>
                          <h6>UV Filter</h6>
                          <Form.Group>
                            <Form.Label>UV Filter</Form.Label>
                            {renderSelect(quote.stationType.uvFilter, buildConfiguredSelectOptions(stationFieldStates.uvFilter, quote.stationType.uvFilter, [], ['YES', 'NO']), value => onFormChange(p => ({
                          ...p,
                          stationType: {
                            ...p.stationType,
                            uvFilter: value
                          }
                        })), stationFieldStates.uvFilter.isDisabled)}
                          </Form.Group>
                        </Card.Body>
                      </Card>}
                  </Col>

                  <Col md={6}>
                    {showElectrical && <Card className="mb-3">
                        <Card.Body>
                          <h6>Electrical</h6>
                          <Row>
                            {stationFieldStates.siteVoltage.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>Site Voltage <span className="text-danger">*</span></Form.Label>{renderSelect(quote.stationType.siteVoltage, buildConfiguredSelectOptions(stationFieldStates.siteVoltage, quote.stationType.siteVoltage, ['sitevoltage'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                siteVoltage: value
                              }
                            })), stationFieldStates.siteVoltage.isDisabled)}</Form.Group></Col>}
                            {stationFieldStates.stationLocation.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>Station Location <span className="text-danger">*</span></Form.Label>{renderSelect(quote.stationType.stationLocation, buildConfiguredSelectOptions(stationFieldStates.stationLocation, quote.stationType.stationLocation, ['stationlocation'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                stationLocation: value
                              }
                            })), stationFieldStates.stationLocation.isDisabled)}</Form.Group></Col>}
                            {stationFieldStates.sccrRating.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>SCCR Rating <span className="text-danger">*</span></Form.Label><Form.Control type="number" value={quote.stationType.sccrRating} disabled={stationFieldStates.sccrRating.isDisabled} onChange={e => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                sccrRating: Number(e.target.value)
                              }
                            }))} onBlur={e => stationTypeBlurValidation('sccrRating', Number(e.target.value))} /></Form.Group></Col>}
                            {stationFieldStates.fccType.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>FCC Type <span className="text-danger">*</span></Form.Label>{renderSelect(quote.stationType.fccType, buildConfiguredSelectOptions(stationFieldStates.fccType, quote.stationType.fccType, ['fcctype'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                fccType: value
                              }
                            })), stationFieldStates.fccType.isDisabled)}</Form.Group></Col>}
                            {stationFieldStates.controlSystemPlatform.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>Control System Platform <span className="text-danger">*</span></Form.Label>{renderSelect(quote.stationType.controlSystemPlatform, buildConfiguredSelectOptions(stationFieldStates.controlSystemPlatform, quote.stationType.controlSystemPlatform, ['controlsystem'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                controlSystemPlatform: value
                              }
                            })), stationFieldStates.controlSystemPlatform.isDisabled)}</Form.Group></Col>}
                            {stationFieldStates.mainPanelLocation.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>Main Panel Location <span className="text-danger">*</span></Form.Label>{renderSelect(quote.stationType.mainPanelLocation, buildConfiguredSelectOptions(stationFieldStates.mainPanelLocation, quote.stationType.mainPanelLocation, ['mainpanel'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                mainPanelLocation: value
                              }
                            })), stationFieldStates.mainPanelLocation.isDisabled)}</Form.Group></Col>}
                            {stationFieldStates.flowMeterLocation.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>Flow Meter Location <span className="text-danger">*</span></Form.Label>{renderSelect(quote.stationType.flowMeterLocation, buildConfiguredSelectOptions(stationFieldStates.flowMeterLocation, quote.stationType.flowMeterLocation, ['flowmeter'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                flowMeterLocation: value
                              }
                            })), stationFieldStates.flowMeterLocation.isDisabled)}</Form.Group></Col>}
                          </Row>
                        </Card.Body>
                      </Card>}

                    {showMechanical && <Card className="mb-3">
                        <Card.Body>
                          <h6>Mechanical</h6>
                          <Row>
                            {stationFieldStates.wetWellDepthFt.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>Wet Well Depth (Ft)</Form.Label><Form.Control type="number" value={quote.stationType.wetWellDepthFt} disabled={stationFieldStates.wetWellDepthFt.isDisabled} onChange={e => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                wetWellDepthFt: Number(e.target.value)
                              }
                            }))} onBlur={e => stationTypeBlurValidation('wetWellDepthFt', Number(e.target.value))} /></Form.Group></Col>}
                            {stationFieldStates.wetWellDepthIn.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>Wet Well Depth (In)</Form.Label><Form.Control type="number" value={quote.stationType.wetWellDepthIn} disabled={stationFieldStates.wetWellDepthIn.isDisabled} onChange={e => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                wetWellDepthIn: Number(e.target.value)
                              }
                            }))} onBlur={e => stationTypeBlurValidation('wetWellDepthIn', Number(e.target.value))} /></Form.Group></Col>}
                            {stationFieldStates.vtPumpBottomClearance.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>VT Pump Bottom Clearance</Form.Label><Form.Control type="number" value={quote.stationType.vtPumpBottomClearance} disabled={stationFieldStates.vtPumpBottomClearance.isDisabled} onChange={e => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                vtPumpBottomClearance: Number(e.target.value)
                              }
                            }))} onBlur={e => stationTypeBlurValidation('vtPumpBottomClearance', Number(e.target.value))} /></Form.Group></Col>}
                            {stationFieldStates.pumpColumnType.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>Pump Column Type</Form.Label>{renderSelect(quote.stationType.pumpColumnType, buildConfiguredSelectOptions(stationFieldStates.pumpColumnType, quote.stationType.pumpColumnType, ['pumpcloumntype', 'pumpcolumntype'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                pumpColumnType: value
                              }
                            })), stationFieldStates.pumpColumnType.isDisabled)}</Form.Group></Col>}
                            {stationFieldStates.baseMaterial.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>Base Material <span className="text-danger">*</span></Form.Label>{renderSelect(quote.stationType.baseMaterial, buildConfiguredSelectOptions(stationFieldStates.baseMaterial, quote.stationType.baseMaterial, ['basemeterial', 'basematerial'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                baseMaterial: value
                              }
                            })), stationFieldStates.baseMaterial.isDisabled)}</Form.Group></Col>}
                            {stationFieldStates.stationColor.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>Station Color <span className="text-danger">*</span></Form.Label>{renderSelect(quote.stationType.stationColor, buildConfiguredSelectOptions(stationFieldStates.stationColor, quote.stationType.stationColor, ['stationcolor'], []), value => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                stationColor: value
                              }
                            })), stationFieldStates.stationColor.isDisabled)}</Form.Group></Col>}
                            {stationFieldStates.baseDimensionL.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>Base Dimension L (in) <span className="text-danger">*</span></Form.Label><Form.Control type="number" value={quote.stationType.baseDimensionL} disabled={stationFieldStates.baseDimensionL.isDisabled} onChange={e => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                baseDimensionL: Number(e.target.value)
                              }
                            }))} onBlur={e => stationTypeBlurValidation('baseDimensionL', Number(e.target.value))} /></Form.Group></Col>}
                            {stationFieldStates.baseDimensionW.isVisible && <Col md={6}><Form.Group className="mb-3"><Form.Label>Base Dimension W (in) <span className="text-danger">*</span></Form.Label><Form.Control type="number" value={quote.stationType.baseDimensionW} disabled={stationFieldStates.baseDimensionW.isDisabled} onChange={e => onFormChange(p => ({
                              ...p,
                              stationType: {
                                ...p.stationType,
                                baseDimensionW: Number(e.target.value)
                              }
                            }))} onBlur={e => stationTypeBlurValidation('baseDimensionW', Number(e.target.value))} /></Form.Group></Col>}
                          </Row>
                        </Card.Body>
                      </Card>}
                  </Col>
                </Row>;
            })()}
            </fieldset>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="2" className="mb-2">
          <Accordion.Header><span className="mx-auto text-px18"><strong>PUMP CONFIGURATION</strong></span></Accordion.Header>
          <Accordion.Body>
            {advancedResult.errors.map((error, index) => <Alert variant="danger" key={index}>{error.message}</Alert>)}
            {advancedRules.error && <Button variant="outline-primary" onClick={advancedRules.reload}>Retry global rules</Button>}
            <fieldset disabled={quote.isProposalGenerated}>
            <div className="table-responsive">
              <Table bordered hover className="mb-3">
                <thead>
                  <tr>
                    <th>QTY</th>
                    <th>Pump Description</th>
                    <th>Motor HP</th>
                    <th>Type</th>
                    <th>GPM</th>
                    <th>VFD</th>
                    <th>XL</th>
                    <th>SS</th>
                    <th>EBV</th>
                    <th>Zone</th>
                    {!quote.isProposalGenerated && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {jockeyFirst(quote.pumpConfigurations).map(pump => <tr key={pump.id}>
                      <td>
                        <Form.Control type="number" size="sm" style={{
                        width: '70px'
                      }} value={pump.qty} onChange={e => onFormChange(p => ({
                        ...p,
                        pumpConfigurations: p.pumpConfigurations.map(entry => entry.id === pump.id ? {
                          ...entry,
                          qty: Number(e.target.value)
                        } : entry)
                      }))} />
                      </td>
                      <td>{renderSelect(pump.pumpDescription, buildSelectOptions(pump.pumpDescription, ['pumpdescription'], []), value => onFormChange(p => ({
                        ...p,
                        pumpConfigurations: p.pumpConfigurations.map(entry => entry.id === pump.id ? {
                          ...entry,
                          pumpDescription: value
                        } : entry)
                      })))}</td>
                      <td>{renderSelect(pump.motorHP, buildSelectOptions(pump.motorHP, ['moterhp', 'motorhp'], []), value => onFormChange(p => ({
                        ...p,
                        pumpConfigurations: p.pumpConfigurations.map(entry => entry.id === pump.id ? {
                          ...entry,
                          motorHP: value
                        } : entry)
                      })))}</td>
                      <td>{renderSelect(pump.type, buildSelectOptions(pump.type, ['pumptype'], []), value => onFormChange(p => ({
                        ...p,
                        pumpConfigurations: p.pumpConfigurations.map(entry => entry.id === pump.id ? {
                          ...entry,
                          type: value
                        } : entry)
                      })))}</td>
                      <td>
                        <Form.Control type="number" size="sm" style={{
                        width: '80px'
                      }} value={pump.gpm} onChange={e => onFormChange(p => ({
                        ...p,
                        pumpConfigurations: p.pumpConfigurations.map(entry => entry.id === pump.id ? {
                          ...entry,
                          gpm: Number(e.target.value)
                        } : entry)
                      }))} />
                      </td>
                      <td>{renderSelect(pump.vfd, buildSelectOptions(pump.vfd, ['vfd'], ['Yes', 'No']), value => onFormChange(p => ({
                        ...p,
                        pumpConfigurations: p.pumpConfigurations.map(entry => entry.id === pump.id ? {
                          ...entry,
                          vfd: value
                        } : entry)
                      })))}</td>
                      <td>{renderSelect(pump.xl, ['Yes', 'No'], value => onFormChange(p => ({
                        ...p,
                        pumpConfigurations: p.pumpConfigurations.map(entry => entry.id === pump.id ? {
                          ...entry,
                          xl: value
                        } : entry)
                      })))}</td>
                      <td>{renderSelect(pump.ss, ['Yes', 'No'], value => onFormChange(p => ({
                        ...p,
                        pumpConfigurations: p.pumpConfigurations.map(entry => entry.id === pump.id ? {
                          ...entry,
                          ss: value
                        } : entry)
                      })))}</td>
                      <td>{renderSelect(pump.ebv, ['Yes', 'No'], value => onFormChange(p => ({
                        ...p,
                        pumpConfigurations: p.pumpConfigurations.map(entry => entry.id === pump.id ? {
                          ...entry,
                          ebv: value
                        } : entry)
                      })))}</td>
                      <td>{renderSelect(pump.zone ?? 1, [1, 2], value => onFormChange(p => ({
                        ...p,
                        pumpConfigurations: p.pumpConfigurations.map(entry => entry.id === pump.id ? {
                          ...entry,
                          zone: Number(value)
                        } : entry)
                      })))}</td>
                      {!quote.isProposalGenerated && <td>
                          <a href="#" className="edit-icon" title="Delete" onClick={e => {
                        e.preventDefault();
                        removePumpConfigurationItem(pump.id);
                      }}>
                            <i className="bi bi-trash"></i>
                          </a>
                        </td>}
                    </tr>)}
                </tbody>
              </Table>
            </div>
            </fieldset>

            <Row className="align-items-center">
              <Col md={6}>
                <p className="mb-1">Station Only Estimated FLA: {quote.pumpSummary.stationOnlyEstimatedFla} A</p>
                <p className="mb-1">Model Number: {quote.pumpSummary.modelNumber}</p>
                {autoPickErrors.length > 0 && <div className="mt-2">
                    {autoPickErrors.map((error, index) => <p key={`${error}-${index}`} className="mb-1 small" style={{
                  color: 'red'
                }}>
                        {error}
                      </p>)}
                  </div>}
              </Col>
              <Col md={6} className="text-end">
                {!quote.isProposalGenerated && <>
                    <Button variant="primary" className="me-2" onClick={loadAutoPick} disabled={isAutoPicking}>
                      {isAutoPicking ? 'AUTO PICKING...' : 'AUTO PICK'}
                    </Button>
                    <Button variant="primary" onClick={addPumpConfigurationItem} disabled={[271, 272].includes(getLookupIdByLabel(['controlpanelorkit'], quote.stationType.panelOrKit))}>
                      ADD NEW
                    </Button>
                  </>}
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="3" className="mb-2">
          <Accordion.Header><span className="mx-auto text-px18"><strong>OPTIONS</strong></span></Accordion.Header>
          <Accordion.Body>
            {!quote.isProposalGenerated && <>
                <Row className="mt-2">
                  <Col md={5}>
                    <h6>Select Category</h6>
                    <Card className="mb-3">
                      <Card.Body>
                        <Form.Group className="mb-3">
                          <Form.Check inline type="radio" name="categoryType" label="HIDE AUTO PICKS" checked={quote.optionsSection.categoryType === 'HIDE AUTO PICKS'} onChange={() => handleCategoryTypeChange('HIDE AUTO PICKS')} />
                          <Form.Check inline type="radio" name="categoryType" label="SHOW ALL" checked={quote.optionsSection.categoryType === 'SHOW ALL'} onChange={() => handleCategoryTypeChange('SHOW ALL')} />
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label>Category</Form.Label>
                          <div className="d-flex align-items-center gap-2">
                            <Button variant="outline-secondary" size="sm" onClick={prevCategory} disabled={categoryList.length <= 1}>
                              &lsaquo;
                            </Button>
                            <div className="flex-grow-1">
                              {categoryList.length > 0 ? <Form.Select value={quote.optionsSection.category} onChange={e => handleCategoryChange(e.target.value)}>
                                  {categoryList.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                                </Form.Select> : <Form.Select value={quote.optionsSection.category} onChange={e => handleCategoryChange(e.target.value)}>
                                  {buildSelectOptions(quote.optionsSection.category, ['category', 'optionpicklistcategory'], ['Controls', 'Electrical', 'Piping']).map(opt => <option key={String(opt)} value={String(opt)}>{opt}</option>)}
                                </Form.Select>}
                            </div>
                            <Button variant="outline-secondary" size="sm" onClick={nextCategory} disabled={categoryList.length <= 1}>
                              &rsaquo;
                            </Button>
                          </div>
                        </Form.Group>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={7}>
                    <h6>Description</h6>
                    <Card className="mb-3">
                      <Card.Body>
                        <p className="mb-0">{quote.optionsSection.description}</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <h6 className="mb-2">OPTION PICK LIST</h6>
                <div className="table-responsive mb-3">
                  <Table bordered hover>
                    <thead>
                      <tr>
                        <th>Option #</th>
                        <th>Description</th>
                        <th>List Price</th>
                        <th>QTY</th>
                        <th>Option</th>
                        <th>Dis. Multiplier</th>
                        <th>Dis. Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quote.optionPickList.map(row => <tr key={row.id}>
                          <td>{row.optionNumber}</td>
                          <td>{row.description}</td>
                          <td>{formatCurrency(row.listPrice)}</td>
                          <td>{row.quantity}</td>
                          <td>{row.optionType}</td>
                          <td>{row.discountMultiplier}</td>
                          <td>{formatCurrency(row.discountPrice)}</td>
                        </tr>)}
                    </tbody>
                  </Table>
                </div>
                <div className="text-end mb-3">
                  <Button variant="primary" className="me-2" onClick={handleSelectOptions}>SELECT</Button>
                  <Button variant="primary" onClick={openCustomOptionModal}>CUSTOM OPTION</Button>
                </div>
                {selectedOptionError && <div className="alert alert-warning mt-2" role="alert">
                    {selectedOptionError}
                  </div>}
              </>}

            <Row className="mb-2">
              <Col>
                <h6 className="mb-0">SELECTED OPTION LIST</h6>
              </Col>
              <Col className="text-end">
                <Button variant="primary" size="sm">Show All</Button>
              </Col>
            </Row>

            <div className="table-responsive">
              <Table bordered hover>
                <thead>
                  <tr>
                    <th>Option #</th>
                    <th>Category</th>
                    <th>Part Name</th>
                    <th>QTY</th>
                    <th>List Price</th>
                    <th>Extended Price</th>
                    <th>BASE</th>
                    <th>ADDL</th>
                    {!quote.isProposalGenerated && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {quote.selectedOptionList.map(row => <tr key={row.id}>
                      <td>{row.optionNumber}</td>
                      <td>{row.category}</td>
                      <td>{row.partName}</td>
                      <td>{row.quantity}</td>
                      <td>{formatCurrency(row.listPrice)}</td>
                      <td>{formatCurrency(row.extendedPrice)}</td>
                      <td>{yesNo(row.base)}</td>
                      <td>{yesNo(row.addl)}</td>
                      {!quote.isProposalGenerated && <td>
                          <a href="#" className="edit-icon me-2" title="Edit" onClick={e => {
                      e.preventDefault();
                      openEditOptionModal(quote.selectedOptionList.findIndex(o => o.id === row.id));
                    }}>
                            <i className="bi bi-pencil-square"></i>
                          </a>
                          <a href="#" className="edit-icon" title="Delete" onClick={e => {
                      e.preventDefault();
                      removeSelectedOption(quote.selectedOptionList.findIndex(o => o.id === row.id));
                    }}>
                            <i className="bi bi-trash"></i>
                          </a>
                        </td>}
                    </tr>)}
                </tbody>
              </Table>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="4" className="mb-2">
          <Accordion.Header><span className="mx-auto text-px18"><strong>COST SUMMARY</strong></span></Accordion.Header>
          <Accordion.Body>
            <Row>
              {}
              <Col md={6}>
                <fieldset disabled={quote.isProposalGenerated}>
                <Card className="mb-3">
                  <Card.Body>
                    <h6>Base Station</h6>
                    <Form.Group className="mb-2">
                      <Form.Label className={costSummary.listPrice > 0 ? 'text-primary' : ''}>List Price</Form.Label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">$</span>
                        <Form.Control type="number" value={costSummary.listPrice} onChange={e => updateCostSummary('listPrice', Number(e.target.value))} placeholder="$0.00" />
                      </div>
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Station Price</Form.Label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">$</span>
                        <Form.Control type="number" value={costSummary.stationPrice} onChange={e => updateCostSummary('stationPrice', Number(e.target.value))} placeholder="$0.00" />
                      </div>
                    </Form.Group>
                    {isAdmin && <>
                        <label className="mb-0 fw-semibold" style={{
                        color: '#00457c'
                      }}>Sales Multiplier:</label>
                        <Row className="mb-2 mt-1">
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label className="small">Percentage</Form.Label>
                              <Form.Control type="number" size="sm" value={costSummary.salesMultiplier} onChange={e => updateCostSummary('salesMultiplier', Number(e.target.value))} placeholder="1" />
                            </Form.Group>
                          </Col>
                          <Col md={8}>
                            <Form.Group>
                              <Form.Label className="small">Amount</Form.Label>
                              <div className="input-group input-group-sm">
                                <span className="input-group-text">$</span>
                                <Form.Control type="number" value={costSummary.salesMultiplierAmount} onChange={e => updateCostSummary('salesMultiplierAmount', Number(e.target.value))} placeholder="0.00" />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>
                      </>}
                    <label className="mb-0 fw-semibold" style={{
                      color: '#00457c'
                    }}>Steel Adjustment:</label>
                    <Row className="mb-2 mt-1">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="small">Percentage</Form.Label>
                          <Form.Control type="number" size="sm" value={costSummary.steelAdjPercent} onChange={e => updateCostSummary('steelAdjPercent', Number(e.target.value))} placeholder="0.00%" />
                        </Form.Group>
                      </Col>
                      <Col md={8}>
                        <Form.Group>
                          <Form.Label className="small">Amount</Form.Label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <Form.Control type="number" value={costSummary.steelAdj} onChange={e => updateCostSummary('steelAdj', Number(e.target.value))} placeholder="$0.00" />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    <label className="mb-0 fw-semibold" style={{
                      color: '#00457c'
                    }}>Outside Commission Total:</label>
                    <Row className="mb-2 mt-1">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="small">Percentage</Form.Label>
                          <Form.Control type="number" size="sm" value={costSummary.outsideCommissionPercent} onChange={e => updateCostSummary('outsideCommissionPercent', Number(e.target.value))} placeholder="0.00%" />
                        </Form.Group>
                      </Col>
                      <Col md={8}>
                        <Form.Group>
                          <Form.Label className="small">Amount</Form.Label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <Form.Control type="number" value={costSummary.outsideCommissionTotal} onChange={e => updateCostSummary('outsideCommissionTotal', Number(e.target.value))} placeholder="$0.00" />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    <label className="mb-0 fw-semibold" style={{
                      color: '#00457c'
                    }}>Rebates:</label>
                    <Row className="mb-2 mt-1">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="small">Percentage</Form.Label>
                          <Form.Control type="number" size="sm" value={costSummary.rebatesPercent} onChange={e => updateCostSummary('rebatesPercent', Number(e.target.value))} placeholder="0.00%" />
                        </Form.Group>
                      </Col>
                      <Col md={8}>
                        <Form.Group>
                          <Form.Label className="small">Amount</Form.Label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <Form.Control type="number" value={costSummary.rebates} onChange={e => updateCostSummary('rebates', Number(e.target.value))} placeholder="$0.00" />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small">Freight</Form.Label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <Form.Control type="number" value={costSummary.freight} onChange={e => updateCostSummary('freight', Number(e.target.value))} placeholder="$0.00" />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small">Overseas Shipment</Form.Label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <Form.Control type="number" value={costSummary.overseasShipment} onChange={e => updateCostSummary('overseasShipment', Number(e.target.value))} placeholder="$0.00" />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small">Setup Allowance</Form.Label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <Form.Control type="number" value={costSummary.setupAllowance} onChange={e => updateCostSummary('setupAllowance', Number(e.target.value))} placeholder="$0.00" />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small">Start Up Allowance</Form.Label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <Form.Control type="number" value={costSummary.startupAllowance} onChange={e => updateCostSummary('startupAllowance', Number(e.target.value))} placeholder="$0.00" />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small">Crane Rental</Form.Label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <Form.Control type="number" value={costSummary.craneRental} onChange={e => updateCostSummary('craneRental', Number(e.target.value))} placeholder="$0.00" />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small">Inspection Fees</Form.Label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <Form.Control type="number" value={costSummary.inspectionFees} onChange={e => updateCostSummary('inspectionFees', Number(e.target.value))} placeholder="$0.00" />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Other Installation Misc.</Form.Label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">$</span>
                        <Form.Control type="number" value={costSummary.otherMisc} onChange={e => updateCostSummary('otherMisc', Number(e.target.value))} placeholder="$0.00" />
                      </div>
                    </Form.Group>
                    <label className="mb-0 fw-semibold" style={{
                      color: '#00457c'
                    }}>Setup Multiplier:</label>
                    <Row className="mb-2 mt-1">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="small">Percentage</Form.Label>
                          <Form.Control type="number" size="sm" value={costSummary.setupMultiplier} onChange={e => updateCostSummary('setupMultiplier', Number(e.target.value))} placeholder="0.00%" />
                        </Form.Group>
                      </Col>
                      <Col md={8}>
                        <Form.Group>
                          <Form.Label className="small">Amount</Form.Label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <Form.Control type="number" value={costSummary.setupTotalValue} onChange={e => updateCostSummary('setupTotalValue', Number(e.target.value))} placeholder="$0.00" />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Taxes</Form.Label>
                      <Form.Control type="number" size="sm" value={costSummary.taxes} onChange={e => updateCostSummary('taxes', Number(e.target.value))} placeholder="$0" />
                    </Form.Group>
                  </Card.Body>
                </Card>
                </fieldset>
              </Col>

              {}
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Body>
                    <h6>Base Station</h6>
                    <Row>
                      <Col md={12}><Form.Group className="mb-2"><Form.Label>List Price</Form.Label><Form.Control value={formatCurrency(quote.costSummary.listPrice)} readOnly /></Form.Group></Col>
                      <Col md={12}><Form.Group className="mb-2"><Form.Label>Station Price</Form.Label><Form.Control value={formatCurrency(quote.costSummary.stationPrice)} readOnly /></Form.Group></Col>
                      <Col md={12}><strong>Sales Multiplier</strong></Col>
                      <Col md={4}><Form.Group className="mb-2"><Form.Label>Percentage</Form.Label><Form.Control value={quote.costSummary.salesMultiplier} readOnly /></Form.Group></Col>
                      <Col md={8}><Form.Group className="mb-2"><Form.Label>Amount</Form.Label><Form.Control value={formatCurrency(quote.costSummary.salesMultiplierAmount)} readOnly /></Form.Group></Col>
                      <Col md={12}><strong>Steel Adjustment</strong></Col>
                      <Col md={4}><Form.Group className="mb-2"><Form.Label>Percentage</Form.Label><Form.Control value={quote.costSummary.steelAdjPercent} readOnly /></Form.Group></Col>
                      <Col md={8}><Form.Group className="mb-2"><Form.Label>Amount</Form.Label><Form.Control value={formatCurrency(quote.costSummary.steelAdjAmount)} readOnly /></Form.Group></Col>
                      <Col md={12}><strong>Outside Commission Total</strong></Col>
                      <Col md={4}><Form.Group className="mb-2"><Form.Label>Percentage</Form.Label><Form.Control value={quote.costSummary.outsideCommissionPercent} readOnly /></Form.Group></Col>
                      <Col md={8}><Form.Group className="mb-2"><Form.Label>Amount</Form.Label><Form.Control value={formatCurrency(quote.costSummary.outsideCommissionAmount)} readOnly /></Form.Group></Col>
                      <Col md={12}><strong>Rebates</strong></Col>
                      <Col md={4}><Form.Group className="mb-2"><Form.Label>Percentage</Form.Label><Form.Control value={quote.costSummary.rebatesPercent} readOnly /></Form.Group></Col>
                      <Col md={8}><Form.Group className="mb-2"><Form.Label>Amount</Form.Label><Form.Control value={formatCurrency(quote.costSummary.rebatesAmount)} readOnly /></Form.Group></Col>
                      <Col md={6}><Form.Group className="mb-2"><Form.Label>Freight</Form.Label><Form.Control value={formatCurrency(quote.costSummary.freight)} readOnly /></Form.Group></Col>
                      <Col md={6}><Form.Group className="mb-2"><Form.Label>Overseas Shipment</Form.Label><Form.Control value={formatCurrency(quote.costSummary.overseasShipment)} readOnly /></Form.Group></Col>
                      <Col md={6}><Form.Group className="mb-2"><Form.Label>Setup Allowance</Form.Label><Form.Control value={formatCurrency(quote.costSummary.setupAllowance)} readOnly /></Form.Group></Col>
                      <Col md={6}><Form.Group className="mb-2"><Form.Label>Start Up Allowance</Form.Label><Form.Control value={formatCurrency(quote.costSummary.startupAllowance)} readOnly /></Form.Group></Col>
                      <Col md={6}><Form.Group className="mb-2"><Form.Label>Crane Rental</Form.Label><Form.Control value={formatCurrency(quote.costSummary.craneRental)} readOnly /></Form.Group></Col>
                      <Col md={6}><Form.Group className="mb-2"><Form.Label>Inspection Fees</Form.Label><Form.Control value={formatCurrency(quote.costSummary.inspectionFees)} readOnly /></Form.Group></Col>
                      <Col md={6}><Form.Group className="mb-2"><Form.Label>Other Installation Misc.</Form.Label><Form.Control value={formatCurrency(quote.costSummary.otherInstallationMisc)} readOnly /></Form.Group></Col>
                      <Col md={12}><strong>Setup Multiplier</strong></Col>
                      <Col md={4}><Form.Group className="mb-2"><Form.Label>Percentage</Form.Label><Form.Control value={quote.costSummary.setupMultiplierPercent} readOnly /></Form.Group></Col>
                      <Col md={8}><Form.Group className="mb-2"><Form.Label>Amount</Form.Label><Form.Control value={formatCurrency(quote.costSummary.setupMultiplierAmount)} readOnly /></Form.Group></Col>
                      <Col md={12}><Form.Group className="mb-2"><Form.Label>Taxes</Form.Label><Form.Control value={formatCurrency(quote.costSummary.taxes)} readOnly /></Form.Group></Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-3">
                  <Card.Body>
                    <h6>Warranty Details</h6>
                    <Form.Group className="mb-2"><Form.Label>Warranty Type</Form.Label>{renderSelect(quote.costSummary.warrantyType, ['Domestic', 'International'], value => onFormChange(p => ({
                      ...p,
                      costSummary: {
                        ...p.costSummary,
                        warrantyType: value
                      }
                    })))}</Form.Group>
                    <Row>
                      <Col md={8}><Form.Group className="mb-2"><Form.Label>Warranty Years</Form.Label>{renderSelect(quote.costSummary.warrantyYears, [2, 3, 4], value => onFormChange(p => ({
                          ...p,
                          costSummary: {
                            ...p.costSummary,
                            warrantyYears: Number(value)
                          }
                        })))}</Form.Group></Col>
                      <Col md={4}><Form.Group className="mb-2"><Form.Label>Amount</Form.Label><Form.Control value={formatCurrency(quote.costSummary.warrantyAmount)} readOnly /></Form.Group></Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Body>
                    <h6>Margin Estimator</h6>
                    <p className="mb-2"><strong>Multiplier Active:</strong> {quote.costSummary.multiplierActive}</p>
                    <p className="mb-1"><strong>Base Station</strong></p>
                    <Row>
                      <Col md={4}><Form.Group className="mb-2"><Form.Label>Gross %</Form.Label><Form.Control value={quote.costSummary.grossMarginPercent} readOnly /></Form.Group></Col>
                      <Col md={8}><Form.Group className="mb-2"><Form.Label>Gross Amount</Form.Label><Form.Control value={formatCurrency(quote.costSummary.grossMarginAmount)} readOnly /></Form.Group></Col>
                      <Col md={4}><Form.Group className="mb-2"><Form.Label>Net %</Form.Label><Form.Control value={quote.costSummary.netMarginPercent} readOnly /></Form.Group></Col>
                      <Col md={8}><Form.Group className="mb-2"><Form.Label>Net Amount</Form.Label><Form.Control value={formatCurrency(quote.costSummary.netMarginAmount)} readOnly /></Form.Group></Col>
                    </Row>
                    <p className="mb-1"><strong>Base & Additional Items</strong></p>
                    <Row>
                      <Col md={4}><Form.Group className="mb-2"><Form.Label>Gross %</Form.Label><Form.Control value={quote.costSummary.grossBaseAddItemMarginPercent} readOnly /></Form.Group></Col>
                      <Col md={8}><Form.Group className="mb-2"><Form.Label>Gross Amount</Form.Label><Form.Control value={formatCurrency(quote.costSummary.grossBaseAddItemMarginAmount)} readOnly /></Form.Group></Col>
                      <Col md={4}><Form.Group className="mb-2"><Form.Label>Net %</Form.Label><Form.Control value={quote.costSummary.netBaseAddItemMarginPercent} readOnly /></Form.Group></Col>
                      <Col md={8}><Form.Group className="mb-2"><Form.Label>Net Amount</Form.Label><Form.Control value={formatCurrency(quote.costSummary.netBaseAddItemMarginAmount)} readOnly /></Form.Group></Col>
                    </Row>
                    <p className="small mb-0">NOTE: Estimate only, actual margins may vary.</p>
                  </Card.Body>
                </Card>

                <div className="mb-3">
                  <Button variant="primary" className="me-2" disabled={isLoadingCostSummary} onClick={() => invokeCostSummaryApi('up')}>PRICE UP</Button>
                  <Button variant="primary" className="me-2" disabled={isLoadingCostSummary} onClick={() => invokeCostSummaryApi('down')}>PRICE DOWN</Button>
                  <Button variant="primary" className="me-2" disabled={isLoadingCostSummary} onClick={() => invokeCostSummaryApi('5%')}>5%</Button>
                  <Button variant="primary" className="me-2" disabled={isLoadingCostSummary} onClick={() => invokeCostSummaryApi('10%')}>10%</Button>
                  <Button variant="primary" disabled={isLoadingCostSummary} onClick={() => {
                  setPriceMatchInput(String(quote.costSummary.invoicePriceAsQuoted));
                  setShowPriceMatchModal(true);
                }}>COMPARE PRICE</Button>
                </div>

                <Card className="mb-3">
                  <Card.Body>
                    <Form.Group className="mb-2"><Form.Label>Final Sales Price</Form.Label><Form.Control value={formatCurrency(quote.costSummary.finalSalePrice)} readOnly /></Form.Group>
                    <Form.Group className="mb-0"><Form.Label>Invoice Price as Quoted</Form.Label><Form.Control value={formatCurrency(quote.costSummary.invoicePriceAsQuoted)} readOnly /></Form.Group>
                  </Card.Body>
                </Card>

                <div className="text-end">
                  <Button variant="primary" disabled={isLoadingCostSummary} onClick={() => invokeCostSummaryApi('refresh')}>
                    {isLoadingCostSummary ? 'REFRESHING...' : 'REFRESH COST SUMMARY'}
                  </Button>
                </div>
              </Col>
            </Row>
            {!quote.isProposalGenerated && <p className="text-center small mb-0">NOTE: After changing any field please refresh cost summary, to update the correct values.</p>}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Card className="mt-4">
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label><strong>General Job Notes</strong></Form.Label>
            <Form.Control as="textarea" rows={6} value={quote.notes} readOnly={quote.isProposalGenerated} onChange={e => onFormChange(p => ({
            ...p,
            notes: e.target.value
          }))} />
          </Form.Group>
          <hr />

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Model Number:</strong></Form.Label>
                <Form.Control type="text" value={quote.modelNumber} readOnly={quote.isProposalGenerated} onChange={e => onFormChange(p => ({
                ...p,
                modelNumber: e.target.value
              }))} onKeyPress={() => setIsSaveEnabled(true)} />
              </Form.Group>
            </Col>
            <Col md={6} className="text-end align-self-end mb-3">
              {!quote.isProposalGenerated && <>
                  <Button variant="primary" className="me-2" disabled={quote.warningEnable || isLoadingWarnings} onClick={loadWarnings}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {isLoadingWarnings ? 'LOADING...' : 'WARNING REPORT'}
                  </Button>
                  <Button variant="success" className="me-2" disabled={!isSaveEnabled || quote.selectedOptionList.length === 0 || isSaving} onClick={() => salesQuoteSubmit(false)}>
                    <i className="bi bi-save me-2"></i>
                    {isSaving ? 'SAVING...' : 'SAVE QUOTE'}
                  </Button>
                  <Button variant="primary" disabled={!isWarningExecuted || isSaving} onClick={() => salesQuoteSubmit(true)}>
                    <i className="bi bi-file-earmark-text me-2"></i>
                    GENERATE PROPOSAL
                  </Button>
                </>}
            </Col>
          </Row>

          {!quote.isProposalGenerated && <Row className="mt-3">
              <Col>
                <h6 className="mb-2">Errors:</h6>
                {quote.hasSalesRepError && <p className="small mb-1" style={{
              color: 'red'
            }}>
                    Please select the sales rep.
                  </p>}
                <hr className="mt-2 mb-3" />
              </Col>
            </Row>}

          {}
          <br />
          <Row>
            <Col>
              <h6 className="mb-2">Warnings:</h6>
              {quote.warningSorted.map(warning => <React.Fragment key={warning.id}>
                  <p className="mb-1 small" style={{
                color: warningTextColor(warning.type)
              }}>
                    {warning.text}
                  </p>
                  <hr className="mt-0 mb-2" />
                </React.Fragment>)}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {}
      <Modal show={showStationChangeWarning} onHide={cancelStationChange} centered>
        <Modal.Header closeButton>
          <Modal.Title>Station Type Change</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Changing the station type will clear all selected options. Do you want to proceed?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelStationChange}>Cancel</Button>
          <Button variant="primary" onClick={confirmStationChange}>Yes, Continue</Button>
        </Modal.Footer>
      </Modal>

      {}
      <Modal show={showVtChangeWarning} onHide={cancelVtChange} centered>
        <Modal.Header closeButton>
          <Modal.Title>VT Type Change</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Changing the VT type will clear all selected options. Do you want to proceed?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelVtChange}>Cancel</Button>
          <Button variant="primary" onClick={confirmVtChange}>Yes, Continue</Button>
        </Modal.Footer>
      </Modal>

      {}
      <Modal show={showPriceMatchModal} onHide={() => setShowPriceMatchModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Compare Price</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Enter your desired sale price ($)</Form.Label>
            <Form.Control type="number" value={priceMatchInput} onChange={e => setPriceMatchInput(e.target.value)} placeholder="Enter price" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPriceMatchModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={() => {
          const userPrice = parseFloat(priceMatchInput);
          if (Number.isFinite(userPrice) && userPrice > 0) {
            setShowPriceMatchModal(false);
            invokeCostSummaryApi('isUserPrice', userPrice);
          }
        }}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>

      {}
      <Modal show={showVersionsModal} onHide={() => setShowVersionsModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Quote Versions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {versions.length === 0 ? <p className="text-muted">No versions found.</p> : <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Quote No</th>
                  <th>Status</th>
                  <th>Proposal</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {versions.map(v => <tr key={v.id}>
                    <td>{v.version ?? '-'}</td>
                    <td>{v.quotationNumber ?? '-'}</td>
                    <td>{v.status ?? '-'}</td>
                    <td>{v.proposalUrl ? <a href={v.proposalUrl} target="_blank" rel="noreferrer">View</a> : '-'}</td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => {
                  setShowVersionsModal(false);
                  navigate(`/sales/quotes/${v.id}`);
                }}>Open</Button>
                    </td>
                  </tr>)}
              </tbody>
            </Table>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVersionsModal(false)}>Close</Button>
          <Button variant="primary" onClick={() => {
          setShowVersionsModal(false);
          createNewVersion();
        }}>Create Version</Button>
        </Modal.Footer>
      </Modal>

      {}
      <Modal show={showEditOptionModal} onHide={() => setShowEditOptionModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Option Description</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Part Name / Description</Form.Label>
            <Form.Control type="text" value={editOptionData.partName} onChange={e => setEditOptionData(prev => ({
            ...prev,
            partName: e.target.value
          }))} placeholder="Enter part name or description" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditOptionModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={saveEditOption}>Save</Button>
        </Modal.Footer>
      </Modal>

      {}
      <Modal show={showCustomOptionModal} onHide={() => setShowCustomOptionModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Custom Option</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <Form.Control type="text" value={quote.optionsSection.category} readOnly />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Part Number</Form.Label>
            <Form.Control type="text" value={customOptionData.partNumber} readOnly />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Name <span className="text-danger">*</span></Form.Label>
            <Form.Control type="text" value={customOptionData.name} onChange={e => setCustomOptionData(prev => ({
            ...prev,
            name: e.target.value
          }))} placeholder="Enter option name" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>List Price <span className="text-danger">*</span></Form.Label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <Form.Control type="number" value={customOptionData.listPrice} onChange={e => setCustomOptionData(prev => ({
              ...prev,
              listPrice: e.target.value
            }))} placeholder="0.00" />
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={3} value={customOptionData.description} onChange={e => setCustomOptionData(prev => ({
            ...prev,
            description: e.target.value
          }))} placeholder="Optional description" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCustomOptionModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={saveCustomOption} disabled={!customOptionData.name.trim() || toFiniteNumber(customOptionData.listPrice, 0) <= 0}>
            Add Option
          </Button>
        </Modal.Footer>
      </Modal>

      {}
      <Modal show={showValidationModal} onHide={() => setShowValidationModal(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Validation Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">{validationMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowValidationModal(false)}>OK</Button>
        </Modal.Footer>
      </Modal>
    </div>;
};
export default SalesQuoteDetail;
